import { useEffect, useState, useRef, useCallback, createContext, useContext } from "react";
import "@/App.css";
import "@/styles/dark-theme.css";
import '@/styles/page-themes.css';
import { LegacyGamesRedirect } from './components/LegacyGamesRedirect';
import { SignInMethods } from './components/auth/SignInMethods';
import { getThemePreference } from './utils/themePreference';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, NavLink, useSearchParams, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { parseOptionalHandicap } from './utils/handicap';
import { apiError } from './utils/apiError';
import { Toaster, toast } from "sonner";
import soundEffects from "./utils/soundEffects";
import { 
  LayoutDashboard, 
  PlusCircle, 
  History, 
  BarChart3, 
  MapPin, 
  LogOut, 
  User,
  Flag,
  Trophy,
  TrendingUp,
  Calendar as CalendarIcon,
  Trash2,
  ChevronRight,
  ChevronDown,
  Camera,
  Upload,
  X,
  CreditCard,
  Check,
  Users,
  Lock,
  Search,
  MessageCircle,
  Send,
  FileText,
  Heart,
  AlertTriangle,
  MoreHorizontal,
  ShieldAlert,
  UserCircle,
  Ban,
  Eye,
  EyeOff,
  Play,
  Settings,
  ArrowLeft,
  Circle,
  Hash,
  Edit,
  Clock,
  Info,
  Bird,
  UserPlus,
  CheckCircle,
  Pencil,
  DollarSign,
  Mail,
  Zap,
  ChevronLeft,
  Home,
  Fingerprint,
  Phone,
  Wallet
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "./components/ui/dialog";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Textarea } from "./components/ui/textarea";
import { Switch } from "./components/ui/switch";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format } from "date-fns";
import ScoreEntryForm from "./components/ScoreEntryForm";
import MatchPlayEventDetail from "./components/MatchPlayEventDetail";
import SkinsEventDetail from "./components/SkinsEventDetail";
import FindGolfersPage from "./components/FindGolfersPage";
import FindGolfersHub from "./components/FindGolfersHub";
import NewUserDashboard from "./components/NewUserDashboard";
import PremiumDashboard from "./components/PremiumDashboard";
import { StartupSplash } from './components/overlays/StartupSplash';
import DashboardPage from "./components/DashboardPage";
import AddScorePage from "./components/AddScorePage";
import HistoryPage from "./components/HistoryPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import SettingsPage from "./components/SettingsPage";
// Extracted page components
import TournamentsPage from "./pages/TournamentsPage";
import MatchPlayPage from "./pages/MatchPlayPage";
import SkinsPage from "./pages/SkinsPage";
import StrokePlayPage from "./pages/StrokePlayPage";
import StatsPage from "./pages/StatsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
// Events
import EventsPage from "./components/EventsPage";
import { ProfileSetupForm } from './components/onboarding/ProfileSetupForm';
import { OnboardingDialog } from './components/onboarding/OnboardingDialog';
import { WalletSetupStep } from './components/onboarding/WalletSetupStep';
import { WalletSetupReturn } from './components/onboarding/WalletSetupReturn';
import { rememberEventReturn, clearEventReturn, useEventReturn } from './components/events/eventReturn';
import EventDetailPage from "./components/EventDetailPage";
// Groups
import GroupsPage from "./components/GroupsPage";
import GroupDetailPage from "./components/GroupDetailPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import CoursesPage from "./pages/CoursesPage";
import { MySettlementsPage } from "./components/Settlements";
import PhoneVerificationModal from "./components/PhoneVerificationModal";
import TestQuickCard from "./components/TestQuickCard";
import GroupRoundLive from "./components/GroupRoundLive";
import ScorecardNavigator from "./components/scorecards/ScorecardNavigator";
import AdminDashboard from "./pages/AdminDashboard";
import SupportPage from './pages/SupportPage';
import AnnouncementPage from './pages/AnnouncementPage';
import HomePreview from "./components/HomePreview";
// New Dark Theme Components
import DarkAppShell from "./components/DarkAppShell";
import SocialFeedPage from "./components/SocialFeedPage";
import FriendsPage from "./components/FriendsPage";
import ChatPage from "./components/ChatPage";
import BuySellPage from "./components/BuySellPage";
import AIToolsPage from "./components/AIToolsPage";
import TermsPage from "./components/TermsPage";
import PrivacyPage from "./components/PrivacyPage";
import ProfilePage from "./components/ProfilePage";
import GolfGifGenerator from "./components/GolfGifGenerator";
import GifProgressIndicator from "./components/GifProgressIndicator";
import GolfShots, { GolfShotsModal } from "./components/GolfShots";
import HookFeed from "./components/HookFeed";
import BookTeeTimePage from "./components/BookTeeTimePage";
import GameInvitePopup from "./components/GameInvitePopup";
import ShareTargetPage from "./components/ShareTargetPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import TournamentOnboarding from "./components/TournamentOnboarding";
import TournamentRulesPage from "./components/TournamentRulesPage";
import PrizeWalletPage from "./components/PrizeWalletPage";
import WalletMockups from "./components/WalletMockups";
import JoinGamePage from "./components/JoinGamePage";
import GolfCoinsPage from "./components/GolfCoinsPage";
import AchievementsPage from "./components/AchievementsPage";
import LeaderboardPage from "./components/LeaderboardPage";
import GolferDiscovery from "./components/GolferDiscovery";
import InvestorPage from "./components/InvestorPage";
import RonRoHelpButton from "./components/RonRoHelpButton";
import TeeTimeMarketplace from "./components/TeeTimeMarketplace";
import CourseRegistrationPage from './pages/CourseRegistrationPage';
import CoursePayoutsPage from './pages/CoursePayoutsPage';
import ProShopAdminPage from './pages/ProShopAdminPage';
import ProShopLoginPage from './pages/ProShopLoginPage';
import CourseBusinessDashboard from './pages/CourseBusinessDashboard';
import Top10Page from './pages/Top10Page';
import { proshopError } from './components/proshop/proshopApi';
import { AdminAccessProvider } from './components/admin/AdminAccessProvider';
import { LocationProvider } from "./hooks/LocationProvider";

// Golf Outing Management Module (Modular - can be extracted as standalone app)
import OutingsPage from "./modules/outings/pages/OutingsPage";
import OutingSetupWizard from "./modules/outings/pages/OutingSetupWizard";
import OutingDetailPage from "./modules/outings/pages/OutingDetailPage";
import OutingLeaderboardPage from "./modules/outings/pages/OutingLeaderboardPage";
import CaptainScorecardPage from "./modules/outings/pages/CaptainScorecardPage";
import JoinOutingPage from "./modules/outings/pages/JoinOutingPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios - disabled withCredentials since we use localStorage tokens
// This avoids CORS issues with wildcard origins
axios.defaults.withCredentials = false;

// Setup axios interceptor GLOBALLY (before any component renders)
// This adds session token from localStorage for auth
axios.interceptors.request.use((config) => {
  const storedToken = localStorage.getItem('parseeker_session_token');
  if (storedToken) {
    config.headers['X-Session-Token'] = storedToken;
  }
  return config;
});

// Google OAuth Callback Handler
const GoogleCallbackPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const handleCallback = async () => {
      // Get the authorization code from URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');
      
      if (errorParam) {
        setError("Google sign-in was cancelled");
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      
      if (!code) {
        setError("No authorization code received");
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      
      try {
        // Exchange code for session
        const response = await fetch(`${API}/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code })
        });
        
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.detail || 'Authentication failed');
        }
        
        const userData = await response.json();
        
        // Store session token
        if (userData.session_token) {
          localStorage.setItem('parseeker_session_token', userData.session_token);
        }
        
        // Set user in app state
        setUser(userData);
        
        // Redirect to home
        navigate('/home');
      } catch (err) {
        console.error("Google callback error:", err);
        setError(err.message || "Authentication failed");
        setTimeout(() => navigate('/'), 3000);
      }
    };
    
    handleCallback();
  }, [navigate, setUser]);
  
  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-500 text-xl mb-4">{error}</div>
            <p className="text-gray-400">Redirecting...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white text-lg">Signing you in with Google...</p>
          </>
        )}
      </div>
    </div>
  );
};

// Safe text renderer - converts **bold** to <strong> safely without innerHTML
const SafeTextWithBold = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <>
      {parts.map((part, index) => 
        index % 2 === 1 ? (
          <strong key={`bold-${index}-${part.slice(0, 10)}`} className="text-stone-800">{part}</strong>
        ) : (
          <span key={`text-${index}-${part.slice(0, 10)}`}>{part}</span>
        )
      )}
    </>
  );
};

// PS Logo Component
const GolfBallLogo = ({ size = "lg" }) => {
  const textSize = size === "lg" ? "text-6xl" : size === "md" ? "text-3xl" : "text-2xl";
  return (
    <div className={`${textSize} font-black tracking-tighter leading-none`} style={{fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '-0.08em'}}>
      <span className="text-red-600">P</span><span className="text-blue-600">S</span>
    </div>
  );
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showWalletSetup, setShowWalletSetup] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  const audioRef = useRef(null);

  // Golf swing chime sound (base64 encoded simple chime)
  const playMessageChime = useCallback(() => {
    try {
      // Create audio context for a pleasant golf-like "ping" sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for the main tone
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Golf ball "ping" sound - clean, bright tone
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 note
      oscillator.frequency.exponentialRampToValueAtTime(1320, audioContext.currentTime + 0.1); // Up to E6
      oscillator.frequency.exponentialRampToValueAtTime(1760, audioContext.currentTime + 0.15); // Up to A6
      
      oscillator.type = 'sine';
      
      // Quick attack, medium decay - like a golf ball hit
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio notification not supported');
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/conversations`);
      const totalUnread = response.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      
      // Play sound if new messages arrived (only if count increased)
      if (totalUnread > lastMessageCount && lastMessageCount > 0) {
        playMessageChime();
      }
      
      setLastMessageCount(totalUnread);
      setUnreadMessages(totalUnread);
    } catch (e) {
      // Silently fail
    }
  }, [user, lastMessageCount, playMessageChime]);

  // Poll for new messages every 30 seconds when user is logged in
  useEffect(() => {
    if (!user) return;
    
    // Initial fetch
    fetchUnreadCount();
    
    // Set up polling interval
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  const checkAuth = useCallback(async () => {
    // First, check if we have user data in localStorage (set during login)
    const storedUser = localStorage.getItem('parseeker_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setSubscription({ is_subscribed: true, trial_expired: false, trial_days_remaining: 999 });
        // Clear stored user after using it
        localStorage.removeItem('parseeker_user');
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Check subscription status
      // DISABLED FOR TESTING - Always set as subscribed
      try {
        // const subResponse = await axios.get(`${API}/subscription/check`);
        // setSubscription(subResponse.data);
        setSubscription({ is_subscribed: true, trial_expired: false, trial_days_remaining: 999 });
      } catch (e) {
        setSubscription({ is_subscribed: true, trial_expired: false, trial_days_remaining: 999 });
      }
      // Check profile
      try {
        const profileResponse = await axios.get(`${API}/profile/me`);
        setProfile(profileResponse.data);
        setShowWalletSetup(Boolean(profileResponse.data.profile_complete && profileResponse.data.wallet_setup_status === 'pending'));
        if (!profileResponse.data.profile_complete) {
          setShowProfileSetup(true);
        }
      } catch (e) {
        setShowProfileSetup(true);
      }
    } catch (error) {
      // Only clear user if no stored session token (indicates real logout)
      const hasToken = localStorage.getItem('parseeker_session_token');
      if (!hasToken) {
        setUser(null);
        setSubscription(null);
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    clearEventReturn();
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear splash shown flag so it shows on next login
    sessionStorage.removeItem('parseeker_splash_shown');
    // Clear session token and user from localStorage
    localStorage.removeItem('parseeker_session_token');
    localStorage.removeItem('parseeker_user');
    setUser(null);
    setSubscription(null);
    setProfile(null);
    setShowWalletSetup(false);
    setShowProfileSetup(false);
  };

  const refreshSubscription = async () => {
    // DISABLED FOR TESTING - Always set as subscribed
    try {
      // const subResponse = await axios.get(`${API}/subscription/check`);
      // setSubscription(subResponse.data);
      setSubscription({ is_subscribed: true, trial_expired: false, trial_days_remaining: 999 });
    } catch (e) {
      setSubscription({ is_subscribed: true, trial_expired: false, trial_days_remaining: 999 });
    }
  };

  const refreshProfile = async () => {
    try {
      const profileResponse = await axios.get(`${API}/profile/me`);
      setProfile(profileResponse.data);
      setShowWalletSetup(Boolean(profileResponse.data.profile_complete && profileResponse.data.wallet_setup_status === 'pending'));
      return profileResponse.data;
    } catch (e) {
      return null;
    }
  };
  
  // Heartbeat to update online status (every 2 minutes when user is logged in)
  useEffect(() => {
    if (!user) return;
    
    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/profile/heartbeat`);
      } catch (e) {
        // Silent fail - online status is not critical
      }
    };
    
    // Send immediately when user logs in
    sendHeartbeat();
    
    // Send every 2 minutes
    const interval = setInterval(sendHeartbeat, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, setUser, loading, logout, checkAuth, 
      subscription, setSubscription, refreshSubscription,
      profile, setProfile, refreshProfile,
      showProfileSetup, setShowProfileSetup,
      showWalletSetup, setShowWalletSetup,
      showOnboarding, setShowOnboarding,
      unreadMessages, setUnreadMessages, fetchUnreadCount, playMessageChime
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Profile Setup Modal (First-time users)
const ProfileSetupModal = () => {
  const { user, profile, refreshProfile, showProfileSetup, setShowProfileSetup, setShowOnboarding, setShowWalletSetup } = useAuth();
  if (!user || !showProfileSetup) return null;
  const complete = async () => {
    await refreshProfile(); setShowProfileSetup(false); setShowWalletSetup(true); setShowOnboarding(false);
  };
  return <OnboardingDialog title="Create Your Profile" testId="profile-setup-dialog"><ProfileSetupForm key={user.user_id} user={user} profile={profile} onComplete={complete} /></OnboardingDialog>;
};

const WalletSetupModal = () => {
  const { user, showProfileSetup, showWalletSetup, refreshProfile, setShowWalletSetup, setShowOnboarding } = useAuth();
  if (!user || showProfileSetup || !showWalletSetup) return null;
  const complete = async () => {
    await refreshProfile(); setShowWalletSetup(false);
    sessionStorage.removeItem('parseeker_splash_shown'); setShowOnboarding(true);
  };
  return <OnboardingDialog title="Golf Wallet setup" testId="wallet-setup-dialog"><WalletSetupStep key={user.user_id} onComplete={complete} /></OnboardingDialog>;
};

const WalletOnboardingReturnPage = () => {
  const { refreshProfile, setShowWalletSetup, setShowOnboarding } = useAuth();
  const navigate = useNavigate();
  const complete = async () => {
    await refreshProfile(); setShowWalletSetup(false);
    sessionStorage.removeItem('parseeker_splash_shown'); setShowOnboarding(true);
    navigate('/home', { replace: true });
  };
  return <WalletSetupReturn onComplete={complete} />;
};

// Player Profile Card - View any user's profile
const PlayerProfileCard = ({ userId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile/${userId}`);
        setProfile(response.data);
      } catch (error) {
        toast.error("Failed to load profile");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    if (userId) {
      fetchProfile();
    }
  }, [userId, onClose]);

  if (!userId) return null;

  return (
    <Dialog open={!!userId} onOpenChange={() => onClose()}>
      <DialogContent className="bg-white max-w-md">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8 border-green-600 border-t-transparent" />
          </div>
        ) : profile?.is_blocking_me ? (
          <div className="text-center py-8">
            <Ban className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">This profile is not available</p>
          </div>
        ) : profile ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center overflow-hidden">
                  {profile.picture ? (
                    <img src={profile.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-green-700" />
                  )}
                </div>
                <div>
                  <DialogTitle className="text-xl">{profile.name}</DialogTitle>
                  {profile.username && (
                    <p className="text-sm text-stone-500">@{profile.username}</p>
                  )}
                  {profile.location && (
                    <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {profile.location}
                    </p>
                  )}
                </div>
              </div>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              {/* Bio */}
              {profile.bio && (
                <p className="text-stone-600 text-sm">{profile.bio}</p>
              )}
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {profile.handicap !== null ? profile.handicap : '--'}
                  </p>
                  <p className="text-xs text-green-600">Handicap</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {profile.rounds_count || 0}
                  </p>
                  <p className="text-xs text-blue-600">Rounds</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">
                    {profile.average_score || '--'}
                  </p>
                  <p className="text-xs text-amber-600">Avg Score</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">
                    {profile.best_score || '--'}
                  </p>
                  <p className="text-xs text-red-600">Best Score</p>
                </div>
              </div>
              
              {/* Trend */}
              {profile.recent_trend && (
                <div className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm ${
                  profile.recent_trend === 'improving' ? 'bg-green-50 text-green-700' :
                  profile.recent_trend === 'declining' ? 'bg-red-50 text-red-700' :
                  'bg-stone-50 text-stone-600'
                }`}>
                  <TrendingUp className={`w-4 h-4 ${profile.recent_trend === 'declining' ? 'rotate-180' : ''}`} />
                  {profile.recent_trend === 'improving' ? 'Improving' :
                   profile.recent_trend === 'declining' ? 'Needs Work' : 'Stable'}
                </div>
              )}
              
              {/* Member since */}
              {profile.member_since && (
                <p className="text-xs text-stone-400 text-center">
                  Member since {format(new Date(profile.member_since), 'MMMM yyyy')}
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="w-full">
                Close
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// Onboarding Tour - Shows for first-time users after profile setup
const OnboardingTour = () => {
  const { user, showProfileSetup, showWalletSetup, showOnboarding, setShowOnboarding, profile, refreshProfile } = useAuth();
  
  // Use sessionStorage to persist splash dismissed state across navigation
  // This ensures splash only shows once per login session, not on every page change
  const [splashDismissed, setSplashDismissed] = useState(() => {
    return sessionStorage.getItem('parseeker_splash_shown') === 'true';
  });
  
  // Check if user has already completed onboarding (from backend profile)
  const hasCompletedOnboarding = profile?.onboarding_completed === true;
  
  // Show splash screen only on initial login, not during navigation
  // Hide if: still in profile setup, no user logged in, or already shown this session
  useEffect(() => {
    if (showOnboarding) { sessionStorage.removeItem('parseeker_splash_shown'); setSplashDismissed(false); }
  }, [showOnboarding]);
  const shouldShow = !showProfileSetup && !showWalletSetup && user && !splashDismissed;

  const dismissSplash = useCallback(() => {
    sessionStorage.setItem('parseeker_splash_shown', 'true');
    setSplashDismissed(true);
    setShowOnboarding(false);
    // Mark onboarding complete in background
    if (!hasCompletedOnboarding) {
      axios.post(`${API}/profile/onboarding-complete`)
        .then(() => refreshProfile())
        .catch(e => console.error("Failed to mark onboarding complete:", e));
    }
  }, [hasCompletedOnboarding, refreshProfile, setShowOnboarding]);

  if (!shouldShow) return null;
  return <StartupSplash onComplete={dismissSplash} />;
};

// Trial Expiry Popup - Shows after 5 days of use
// DISABLED: Subscription popups disabled during development
const TrialExpiryPopup = () => {
  // Temporarily disabled - return null immediately
  return null;
};

// Login Subscribe Popup - Shows on every login during trial (before expiry)
// DISABLED: Subscription popups disabled during development
const LoginSubscribePopup = () => {
  // Temporarily disabled - return null immediately
  return null;
};

// Edit Profile Modal - For editing user's own profile
const EditProfileModal = ({ isOpen, onClose, onSave }) => {
  const { profile, refreshProfile, user } = useAuth();
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [zelleId, setZelleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && profile) {
      setUsername(profile.username || '');
      setBio(profile.bio || '');
      setLocation(profile.location || '');
      setVenmoUsername(profile.venmo_username || '');
      setZelleId(profile.zelle_id || '');
      setError('');
    }
  }, [isOpen, profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const updateData = {
        username: username.trim(),
        bio: bio.trim(),
        location: location.trim(),
        venmo_username: venmoUsername.trim() || null,
        zelle_id: zelleId.trim() || null
      };
      
      await axios.put(`${API}/profile/me`, updateData);
      await refreshProfile();
      toast.success('Profile updated successfully!');
      onClose();
      if (onSave) onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Edit Profile</CardTitle>
              <CardDescription>Update your golfer profile information</CardDescription>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            
            {/* Profile Picture Preview */}
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-green-700">
                    {user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <div>
                <p className="font-semibold text-stone-900">{user?.name}</p>
                <p className="text-sm text-stone-500">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-username">Username *</Label>
              <Input
                id="edit-username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="golfer123"
                maxLength={20}
                data-testid="edit-profile-username"
              />
              <p className="text-xs text-stone-500">Letters, numbers, and underscores only</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-bio">Bio</Label>
              <Textarea
                id="edit-bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell other golfers about yourself..."
                maxLength={200}
                rows={3}
                data-testid="edit-profile-bio"
              />
              <p className="text-xs text-stone-500">{bio.length}/200 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, State"
                maxLength={50}
                data-testid="edit-profile-location"
              />
            </div>
            
            {/* Venmo Username for Settlements */}
            <div className="space-y-2">
              <Label htmlFor="edit-venmo" className="flex items-center gap-2">
                <span>Venmo Username</span>
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">For game payouts</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">@</span>
                <Input
                  id="edit-venmo"
                  value={venmoUsername}
                  onChange={(e) => setVenmoUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                  placeholder="your-venmo-handle"
                  maxLength={30}
                  className="pl-8"
                  data-testid="edit-profile-venmo"
                />
              </div>
              <p className="text-xs text-stone-500">Used for collecting winnings from games</p>
            </div>
            
            {/* Zelle ID for Settlements */}
            <div className="space-y-2">
              <Label htmlFor="edit-zelle" className="flex items-center gap-2">
                <span>Zelle Email/Phone</span>
                <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">For game payouts</span>
              </Label>
              <Input
                id="edit-zelle"
                value={zelleId}
                onChange={(e) => setZelleId(e.target.value)}
                placeholder="email@example.com or phone"
                maxLength={50}
                data-testid="edit-profile-zelle"
              />
              <p className="text-xs text-stone-500">Email or phone number registered with Zelle</p>
            </div>
            
            {/* Handicap Info - Read Only */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800">Handicap Index</p>
              <p className="text-xs text-green-600 mt-1">
                Your handicap is automatically calculated from your recorded rounds. 
                Play more rounds to establish or update your handicap!
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary flex-1" disabled={loading}>
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// User Profile View Modal
const UserProfileModal = ({ userId, onClose }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocking, setBlocking] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [requestingJoin, setRequestingJoin] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile/${userId}`);
        setProfile(response.data);
      } catch (error) {
        toast.error('Failed to load profile');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, onClose]);

  // Fetch user's leagues
  useEffect(() => {
    const fetchLeagues = async () => {
      if (!userId) return;
      setLoadingLeagues(true);
      try {
        const response = await axios.get(`${API}/profile/${userId}/leagues`);
        setLeagues(response.data.leagues || []);
      } catch (error) {
        console.error('Failed to load leagues:', error);
      } finally {
        setLoadingLeagues(false);
      }
    };
    fetchLeagues();
  }, [userId]);

  const handleRequestJoin = async (eventId, eventName) => {
    setRequestingJoin(eventId);
    try {
      await axios.post(`${API}/leagues/${eventId}/request-join`);
      toast.success(`Request to join "${eventName}" sent!`);
      // Mark league as requested
      setLeagues(prev => prev.map(l => 
        l.event_id === eventId ? { ...l, requested: true } : l
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequestingJoin(null);
    }
  };

  const handleBlock = async () => {
    if (!window.confirm(`Block ${profile?.name}? They won't be able to message you.`)) return;
    setBlocking(true);
    try {
      await axios.post(`${API}/block`, { user_id: userId });
      setProfile(prev => ({ ...prev, is_blocked_by_me: true }));
      toast.success('User blocked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    setBlocking(true);
    try {
      await axios.delete(`${API}/block/${userId}`);
      setProfile(prev => ({ ...prev, is_blocked_by_me: false }));
      toast.success('User unblocked');
    } catch (error) {
      toast.error('Failed to unblock user');
    } finally {
      setBlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-black">
              <span className="text-red-600">Player</span> <span className="text-blue-600">Profile</span>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {profile?.is_blocking_me ? (
            <div className="text-center py-8">
              <Ban className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">This profile is not available.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Avatar and Name - Blue background with red initial */}
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  {profile?.picture ? (
                    <img src={profile.picture} alt="" className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <span className="text-red-400 text-3xl font-black">
                      {profile?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-stone-900">{profile?.name}</h3>
                {profile?.username && (
                  <p className="text-stone-500">@{profile.username}</p>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 py-4 border-y">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-black text-blue-700">
                    {profile?.handicap !== null ? profile.handicap : '--'}
                  </p>
                  <p className="text-xs font-bold text-red-600">Handicap</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-black text-red-700">{profile?.rounds_count || 0}</p>
                  <p className="text-xs font-bold text-blue-600">Rounds</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-black text-blue-700">
                    {profile?.average_score || '--'}
                  </p>
                  <p className="text-xs font-bold text-red-600">Avg Score</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-black text-red-700">
                    {profile?.best_score || '--'}
                  </p>
                  <p className="text-xs font-bold text-blue-600">Best Score</p>
                </div>
              </div>

              {/* Trend */}
              {profile?.recent_trend && (
                <div className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold ${
                  profile.recent_trend === 'improving' ? 'bg-blue-50 text-blue-700' :
                  profile.recent_trend === 'declining' ? 'bg-red-50 text-red-700' :
                  'bg-stone-50 text-stone-600'
                }`}>
                  <TrendingUp className={`w-4 h-4 ${profile.recent_trend === 'declining' ? 'rotate-180' : ''}`} />
                  {profile.recent_trend === 'improving' ? 'Improving' :
                   profile.recent_trend === 'declining' ? 'Needs Work' : 'Stable'}
                </div>
              )}

              {/* Bio */}
              {profile?.bio && (
                <div>
                  <p className="text-sm text-stone-600">{profile.bio}</p>
                </div>
              )}

              {/* Location */}
              {profile?.location && (
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <MapPin className="w-4 h-4" />
                  {profile.location}
                </div>
              )}

              {/* Member since */}
              {profile?.member_since && (
                <p className="text-xs text-stone-400">
                  Member since {new Date(profile.member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              )}

              {/* Leagues Section */}
              {leagues.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-black mb-3">
                    <span className="text-stone-700">Active</span> <span className="text-blue-600">Leagues</span>
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {leagues.map((league) => (
                      <div 
                        key={league.event_id} 
                        className="flex items-center justify-between p-2 bg-stone-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{league.name}</p>
                          <p className="text-xs text-stone-500">
                            {league.player_count} players · {league.role === 'organizer' ? 'Organizer' : 'Player'}
                          </p>
                        </div>
                        {userId !== user?.user_id && league.creator_id === userId && !league.requested && (
                          <button
                            onClick={() => handleRequestJoin(league.event_id, league.name)}
                            disabled={requestingJoin === league.event_id}
                            className="ml-2 px-2 py-1 text-xs font-bold text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          >
                            {requestingJoin === league.event_id ? (
                              <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              'Request to Join'
                            )}
                          </button>
                        )}
                        {league.requested && (
                          <span className="ml-2 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded">
                            Requested
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loadingLeagues && (
                <div className="flex justify-center py-2">
                  <div className="w-4 h-4 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin" />
                </div>
              )}

              {/* Actions */}
              {userId !== user?.user_id && (
                <div className="pt-4 border-t">
                  {profile?.is_blocked_by_me ? (
                    <Button
                      onClick={handleUnblock}
                      disabled={blocking}
                      variant="outline"
                      className="w-full"
                    >
                      {blocking ? <div className="spinner w-4 h-4" /> : 'Unblock User'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleBlock}
                      disabled={blocking}
                      variant="outline"
                      className="w-full text-red-600 hover:bg-red-50"
                    >
                      {blocking ? <div className="spinner w-4 h-4" /> : (
                        <>
                          <Ban className="w-4 h-4 mr-2" />
                          Block User
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Invite Landing Page
const InviteLandingPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await axios.get(`${API}/invites/${inviteCode}`);
        setInviteData(response.data);
        // Store invite code for post-registration connection
        localStorage.setItem('parseeker_invite_code', inviteCode);
      } catch (err) {
        setError("This invite link is invalid or has expired");
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [inviteCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="spinner w-8 h-8 border-green-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-800 mb-2">Invalid Invite</h2>
            <p className="text-stone-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-green-700 hover:bg-green-800">
              Go to Par Seeker
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-stone-50 flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <img 
          src="/images/par-seeker-logo-full.png" 
          alt="Par Seeker" 
          className="w-32 h-32 object-contain mx-auto mb-6"
        />
        
        {/* Invite Message */}
        <Card className="mb-6 border-2 border-green-100 shadow-lg">
          <CardContent className="p-8">
            <h1 className="text-2xl font-black text-green-800 mb-3">
              You've Been Invited!
            </h1>
            <p className="text-lg text-stone-600 mb-4">
              <span className="font-bold text-green-700">{inviteData?.inviter_name || 'A friend'}</span> wants you to join Par Seeker
            </p>
            <div className="w-16 h-1 bg-green-600 mx-auto rounded-full mb-4" />
            <p className="text-stone-500 text-sm">
              Track your golf handicap, organize games, and settle up with friends.
            </p>
          </CardContent>
        </Card>
        
        {/* CTA Buttons */}
        <Button
          onClick={() => navigate('/?auth=signup')}
          className="w-full h-14 bg-green-700 hover:bg-green-800 text-lg font-bold mb-3"
          data-testid="invite-create-account-btn"
        >
          Create Free Account
        </Button>
        
        <button
          onClick={() => navigate('/')}
          className="text-green-700 hover:text-green-800 font-medium"
        >
          Already have an account? Sign In
        </button>
        
        {/* Tagline */}
        <p className="mt-8 text-sm text-stone-400 font-medium">
          Your Golf Game, Micro Managed
        </p>
      </div>
    </div>
  );
};

// Direct sign-in entry; the former Launch screen is no longer part of routing.
const LoginPage = ({ proshop = false }) => {
  const { user, checkAuth, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(() => new URLSearchParams(location.search).get('auth') !== 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEmail, setBiometricEmail] = useState('');
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [registrationPhoneVerified, setRegistrationPhoneVerified] = useState(false);
  const [registrationPhone, setRegistrationPhone] = useState('');
  const [authError, setAuthError] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const emailAuthLock = useRef(false);
  const recoveryLock = useRef(false);
  useEffect(() => {
    setIsLogin(proshop || new URLSearchParams(location.search).get('auth') !== 'signup');
    setAuthError(''); setPassword('');
  }, [proshop, location.search]);

  // Check if WebAuthn is supported
  const isWebAuthnSupported = () => {
    return window.PublicKeyCredential !== undefined;
  };

  // Load saved credentials on mount and check biometric availability
  useEffect(() => {
    if (proshop) return;
    const savedEmail = localStorage.getItem('parseeker_remembered_email');
    const savedPassword = localStorage.getItem('parseeker_remembered_password');
    const savedRememberMe = localStorage.getItem('parseeker_remember_me') === 'true';
    const savedBiometricEmail = localStorage.getItem('parseeker_biometric_email');
    
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
      // Also restore password if saved
      if (savedPassword) {
        try {
          // Decode the saved password (base64 encoded for basic obfuscation)
          setPassword(atob(savedPassword));
        } catch {
          // If decoding fails, clear the saved password
          localStorage.removeItem('parseeker_remembered_password');
        }
      }
    }
    
    // Check if biometric login is available
    if (savedBiometricEmail && isWebAuthnSupported()) {
      setBiometricEmail(savedBiometricEmail);
      checkBiometricAvailable(savedBiometricEmail);
    }
  }, [proshop]);

  const checkBiometricAvailable = async (emailToCheck) => {
    try {
      const response = await axios.post(`${API}/auth/biometric/check-available`, { email: emailToCheck });
      setBiometricAvailable(response.data.available);
    } catch {
      setBiometricAvailable(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!biometricEmail || !isWebAuthnSupported()) return;
    
    setBiometricLoading(true);
    try {
      // Get challenge from server
      const challengeResponse = await axios.post(`${API}/auth/biometric/challenge`, { email: biometricEmail });
      const { challenge, credential_ids } = challengeResponse.data;
      
      // Convert challenge to ArrayBuffer
      const challengeBuffer = Uint8Array.from(atob(challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      
      // Create allowCredentials from registered credential IDs
      const allowCredentials = credential_ids.map(id => ({
        id: Uint8Array.from(atob(id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        type: 'public-key',
        transports: ['internal']
      }));
      
      // Request biometric authentication
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: challengeBuffer,
          allowCredentials,
          userVerification: 'required',
          timeout: 60000
        }
      });
      
      // Send credential to server for verification
      const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      
      const verifyResponse = await axios.post(`${API}/auth/biometric/verify`, {
        email: biometricEmail,
        credential_id: credentialId,
        client_data: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
        authenticator_data: btoa(String.fromCharCode(...new Uint8Array(credential.response.authenticatorData))),
        signature: btoa(String.fromCharCode(...new Uint8Array(credential.response.signature)))
      });
      
      await checkAuth();
      toast.success("Signed in with Face ID");
      navigate('/home');
    } catch (error) {
      console.error("Biometric login error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Face ID cancelled or not available");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Face ID login failed. Try signing in with password.");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Get the Google OAuth redirect URL from our backend
      const response = await fetch(`${API}/auth/google`);
      const data = await response.json();
      
      if (data.redirect_url) {
        // Redirect to Google consent screen
        window.location.href = data.redirect_url;
      } else {
        toast.error("Failed to initiate Google login");
      }
    } catch (error) {
      console.error("Google login error:", error);
      toast.error("Failed to connect to Google");
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (emailAuthLock.current) return;
    setAuthError('');
    if (!email || !password || (!isLogin && !name)) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate password confirmation for registration
    if (!isLogin) {
      if (password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      // Phone verification is now optional - don't block registration
    }

    emailAuthLock.current = true;
    setLoading(true);
    
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      // Only include phone if verified
      const payload = isLogin 
        ? { email, password }
        : { 
            email, 
            password, 
            name, 
            ...(registrationPhoneVerified && registrationPhone ? { phone_number: registrationPhone, country_code: '+1' } : {})
          };
      
      // Use native fetch instead of axios for better Safari compatibility
      // Using 'omit' for credentials since we use localStorage tokens, not cookies
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle API errors
        const detail = data?.detail;
        let msg;
        if (detail === "Email already registered") {
          msg = "This email is already registered. Please sign in instead.";
        } else if (detail === "Invalid credentials" || detail === "Invalid email or password") {
          msg = "Invalid email or password";
        } else {
          msg = detail ? proshopError({ response: { data: { detail } } }) : (isLogin ? "Login failed" : "Registration failed");
        }
        setAuthError(msg);
        if (!proshop) toast.error(msg);
        if (proshop) setPassword('');
        setLoading(false);
        return;
      }
      
      // Store session token in localStorage as Safari cookie fallback
      if (proshop && (!data.session_token || !data.user_id)) throw new Error('SESSION_MISSING');
      if (data.session_token) {
        localStorage.setItem('parseeker_session_token', data.session_token);
      }
      
      // Store user data in localStorage for immediate access after redirect
      if (data.user_id) {
        localStorage.setItem('parseeker_user', JSON.stringify({
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          picture: data.picture
        }));
      }
      
      // Save or clear remembered credentials based on checkbox
      // Always update password on successful login if remember me is checked
      if (!proshop && isLogin && rememberMe) {
        localStorage.setItem('parseeker_remembered_email', email);
        localStorage.setItem('parseeker_remember_me', 'true');
        // Save password with base64 encoding (basic obfuscation, not encryption)
        localStorage.setItem('parseeker_remembered_password', btoa(password));
      } else {
        localStorage.removeItem('parseeker_remembered_email');
        localStorage.removeItem('parseeker_remember_me');
        localStorage.removeItem('parseeker_remembered_password');
      }
      
      // Phone verification removed - proceed directly to home
      // Users can add phone later in Settings if needed
      
      // Redirect after successful login
      setLoading(false);
      window.location.href = proshop ? '/proshop?continue=1' : '/home';
      
    } catch (error) {
      const message = error.message === 'SESSION_MISSING' ? 'Unable to start your session. Please try again.' : 'Unable to sign in. Check your connection and try again.';
      setAuthError(message);
      if (!proshop) toast.error(message);
      if (proshop) setPassword('');
      setLoading(false);
    } finally {
      emailAuthLock.current = false;
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (recoveryLock.current) return;
    setForgotPasswordError('');
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    recoveryLock.current = true;
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      setForgotPasswordSent(true);
      if (!proshop) toast.success("Check your email for reset instructions");
    } catch (error) {
      const message = proshopError(error);
      setForgotPasswordError(message);
      if (!proshop) toast.error(message);
    } finally {
      setForgotPasswordLoading(false);
      recoveryLock.current = false;
    }
  };

  // Handle phone verification completion (for existing users)
  const handlePhoneVerified = (phoneNumber) => {
    setShowPhoneVerification(false);
    setPendingLoginData(null);
    // Redirect to home after phone verification
    window.location.href = '/home';
  };

  // Handle phone verification during registration
  const handleRegistrationPhoneVerified = (phoneNumber) => {
    setRegistrationPhoneVerified(true);
    setRegistrationPhone(phoneNumber);
    setShowPhoneVerification(false);
  };

  if (proshop) return <ProShopLoginPage user={user} authLoading={authLoading} onLogout={logout}
    login={{ email, setEmail, password, setPassword, showPassword, setShowPassword, loading, error: authError, onSubmit: handleEmailAuth }}
    recovery={{ show: showForgotPassword, setShow: value => { setShowForgotPassword(value); setForgotPasswordSent(false); setForgotPasswordError(''); if (value) rememberEventReturn('/proshop'); }, email: forgotPasswordEmail, setEmail: setForgotPasswordEmail, loading: forgotPasswordLoading, sent: forgotPasswordSent, error: forgotPasswordError, onSubmit: handleForgotPassword }} />;

  if (authLoading) return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center" role="status" data-testid="login-session-loading">
      <span className="text-[var(--ps-text-secondary)]">Loading your account…</span>
    </div>
  );
  if (user) return <Navigate to="/home" replace />;

  return (
    <main className="ps-theme-page min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 py-6" data-testid="login-page">
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="forgot-password-title" data-testid="forgot-password-dialog">
          <Card className="w-full max-w-md animate-fade-in max-h-[90dvh] overflow-y-auto">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-700" />
              </div>
              <CardTitle className="text-xl" id="forgot-password-title">Reset Your Password</CardTitle>
            </CardHeader>
            <CardContent>
              {!forgotPasswordSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
                  <p className="text-stone-600 text-sm text-center mb-4">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-12"
                      data-testid="forgot-password-email"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-green-700 hover:bg-green-800"
                    disabled={forgotPasswordLoading}
                    data-testid="forgot-password-submit"
                  >
                    {forgotPasswordLoading ? (
                      <div className="spinner w-5 h-5 border-white border-t-transparent" />
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordSent(false);
                    }}
                    data-testid="forgot-password-back"
                  >
                    Back to Sign In
                  </Button>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Check className="w-8 h-8 text-green-700" />
                  </div>
                  <p className="text-stone-600">
                    We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                  </p>
                  <p className="text-stone-500 text-sm">
                    Check your inbox and follow the instructions to reset your password.
                  </p>
                  <Button
                    className="w-full bg-green-700 hover:bg-green-800"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordSent(false);
                    }}
                    data-testid="forgot-password-sent-back"
                  >
                    Back to Sign In
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      <div className="text-center animate-fade-in w-full max-w-md relative" data-testid="login-content">
        <div className="relative mb-4">
          <div className="absolute top-0 right-0"><PWAInstallPrompt variant="compact" /></div>
          <img src="/images/par-seeker-logo-full.png" alt="Par Seeker" className="w-28 h-28 object-contain mx-auto" data-testid="par-seeker-auth-logo" />
        </div>
          <div className="space-y-4 w-full">
            {/* Text Logo at top for sign-in form */}
            <div className="text-center mb-4">
              <h1 className="text-3xl font-bold text-[var(--ps-text-primary)]" data-testid="login-heading">
                {isLogin ? "Welcome Back" : "Create Account"}
              </h1>
              <p className="text-[var(--ps-text-secondary)] text-sm mt-1">
                {isLogin ? "Sign in to continue your golf journey" : "Start tracking your game today"}
              </p>
            </div>

            <SignInMethods onGoogle={handleGoogleLogin} onBiometric={handleBiometricLogin}
              biometricAvailable={!!(biometricAvailable && biometricEmail)} biometricLoading={biometricLoading} disabled={loading} />

            {/* Auth Form - clean card */}
            <Card className="text-left border border-slate-200 shadow-sm rounded-xl overflow-hidden">
              <CardContent className="p-5 bg-white">
                {/* Required fields notice - only show on registration */}
                {!isLogin && (
                  <p className="text-xs text-stone-500 mb-3">* Required fields</p>
                )}
                <form onSubmit={handleEmailAuth} className="space-y-3" data-testid="email-auth-form">
                  {!isLogin && (
                    <div className="space-y-1">
                      <Label htmlFor="name" className="text-stone-600 text-sm">Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="name"
                        type="text"
                        autoComplete="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        data-testid="name-input"
                        className="h-11 border-stone-200 focus:border-blue-500 focus:ring-blue-500"
                        style={{ backgroundColor: 'white', color: '#1c1917' }}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="email" className="text-stone-600 text-sm">Email {!isLogin && <span className="text-red-500">*</span>}</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="email-input"
                      className="h-11 border-stone-200 focus:border-blue-500 focus:ring-blue-500"
                      style={{ backgroundColor: 'white', color: '#1c1917' }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="password" className="text-stone-600 text-sm">Password {!isLogin && <span className="text-red-500">*</span>}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete={isLogin ? 'current-password' : 'new-password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        data-testid="password-input"
                        className="h-11 border-stone-200 focus:border-blue-500 focus:ring-blue-500 pr-12"
                        style={{ backgroundColor: 'white', color: '#1c1917' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                        data-testid="toggle-password-visibility"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {!isLogin && (
                      <p className="text-xs text-stone-500">At least 6 characters</p>
                    )}
                  </div>
                  
                  {/* Confirm Password - only show on registration */}
                  {!isLogin && (
                    <div className="space-y-1">
                      <Label htmlFor="confirm-password" className="text-stone-600 text-sm">Confirm Password <span className="text-red-500">*</span></Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        data-testid="confirm-password-input"
                        className="h-11 border-stone-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  
                  {/* Phone Number - only show on registration (NOW OPTIONAL) */}
                  {!isLogin && (
                    <div className="space-y-1">
                      <Label className="text-stone-600 text-sm">Phone Number <span className="text-stone-400 text-xs">(Optional)</span></Label>
                      {registrationPhoneVerified ? (
                        <div className="flex items-center gap-2 h-12 px-3 bg-stone-100 border border-stone-200 rounded-lg">
                          <Check className="w-5 h-5 text-green-600" />
                          <span className="text-stone-700 text-sm">{registrationPhone}</span>
                          <span className="text-green-600 text-xs ml-auto">Verified</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (!email) {
                              toast.error("Please enter your email first");
                              return;
                            }
                            setShowPhoneVerification(true);
                          }}
                          className="w-full flex items-center gap-2 h-12 px-3 bg-stone-800 border border-stone-700 rounded-lg text-stone-400 hover:bg-stone-700 transition-colors"
                          data-testid="add-phone-btn"
                        >
                          <Phone className="w-5 h-5" />
                          <span>Add phone (optional)</span>
                        </button>
                      )}
                      <p className="text-xs text-stone-500">SMS verification temporarily unavailable</p>
                    </div>
                  )}
                  
                  {/* Remember Me checkbox - only show on login */}
                  {isLogin && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="remember-me"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded border-stone-300 focus:ring-green-500"
                        data-testid="remember-me-checkbox"
                      />
                      <label htmlFor="remember-me" className="text-sm text-stone-600 cursor-pointer">
                        Remember me
                      </label>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="ps-on-color w-full py-4 bg-green-700 rounded-xl font-black hover:scale-105 transition-all disabled:opacity-50 uppercase tracking-tight"
                    data-testid="submit-auth-btn"
                    style={{ 
                      fontFamily: 'var(--ps-font-heading, system-ui)',
                      boxShadow: '0 0 20px rgba(34, 197, 94, 0.5), 0 0 40px rgba(34, 197, 94, 0.3)',
                      textShadow: '0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700',
                      fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                      color: '#FFFFFF'
                    }}
                  >
                    {loading ? (
                      <div className="spinner w-5 h-5 border-white border-t-transparent mx-auto" />
                    ) : (
                      isLogin ? 'SIGN IN' : 'CREATE ACCOUNT'
                    )}
                  </button>
                  
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="w-full mt-3 text-sm text-green-700 hover:text-green-800 font-medium"
                      data-testid="forgot-password-btn"
                    >
                      Forgot your password?
                    </button>
                  )}
                </form>
                
                <div className="mt-5 pt-5 border-t border-stone-100 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setRegistrationPhoneVerified(false);
                      setRegistrationPhone('');
                    }}
                    className="text-sm text-stone-500 hover:text-stone-700"
                    data-testid="toggle-auth-mode"
                  >
                    {isLogin ? (
                      <>New here? <span className="text-green-600 font-medium">Create an account</span></>
                    ) : (
                      <>Already have an account? <span className="text-green-600 font-medium">Sign in</span></>
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
            
            {/* Footer Text - compact */}
            <div className="text-center space-y-1 pt-1">
              <p className="text-[var(--ps-text-secondary)] text-xs font-medium">
                Secure. Private. Built for golfers.
              </p>
              <p className="text-[var(--ps-text-muted)] text-xs uppercase">
                PHASE.1.ROLLOUT.PARSEEKER.BETA.V.09
              </p>
            </div>
          </div>
      </div>

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showPhoneVerification}
        onClose={() => {
          // Allow skipping - redirect to home
          setShowPhoneVerification(false);
          if (isLogin) {
            window.location.href = '/home';
          }
        }}
        onVerified={isLogin ? handlePhoneVerified : handleRegistrationPhoneVerified}
        isRegistration={!isLogin}
        email={email}
      />
    </main>
  );
};

// Auth Callback
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        // Use native fetch for better Safari compatibility
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });
        
        if (!response.ok) {
          throw new Error('Authentication failed');
        }
        
        const userData = await response.json();
        
        // Store session token in localStorage as Safari fallback
        if (userData.session_token) {
          localStorage.setItem('parseeker_session_token', userData.session_token);
        }
        
        // Store user data for immediate access
        localStorage.setItem('parseeker_user', JSON.stringify({
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture
        }));
        
        setUser(userData);
        
        // Clear hash and navigate to HOME (not dashboard)
        window.history.replaceState(null, '', '/home');
        window.location.href = '/home';
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate('/');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-100">
      <div className="text-center">
        <div className="spinner mx-auto mb-4" />
        <p className="text-stone-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      rememberEventReturn(['/onboarding/wallet', '/proshop/payouts'].includes(location.pathname) ? `${location.pathname}${location.search}` : location.pathname);
      navigate(location.pathname.startsWith('/proshop/') ? '/proshop' : '/');
    }
  }, [user, loading, navigate, location.state, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="spinner" />
      </div>
    );
  }

  if (!user && !location.state?.user) return null;

  return children;
};

// Layout with Navigation
const DashboardLayout = ({ children }) => {
  const { user, logout, unreadMessages, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showEditProfile, setShowEditProfile] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/add-score', icon: Play, label: 'Play' },
    { path: '/settlements', icon: DollarSign, label: 'Settle Up' },
    { path: '/history', icon: History, label: 'History' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="dashboard-container">
      {/* Profile Setup Modal - First priority */}
      <ProfileSetupModal />
      <WalletSetupModal />
      
      {/* Onboarding Tour - Second priority (after profile setup) */}
      <OnboardingTour />
      
      {/* Edit Profile Modal */}
      <EditProfileModal 
        isOpen={showEditProfile} 
        onClose={() => setShowEditProfile(false)}
      />
      
      {/* Login Subscribe Popup - Shows after onboarding during trial */}
      <LoginSubscribePopup />
      
      {/* Trial Expiry Popup - Shows when trial ends */}
      <TrialExpiryPopup />
      
      {/* Desktop Sidebar - 3D Style */}
      <aside className="hidden md:block sidebar sidebar-3d">
        <div className="flex items-center gap-3 mb-8">
          <span className="text-2xl font-black tracking-tight uppercase">
            <span className="text-green-700">Par</span> <span className="text-amber-500">Seeker</span>
          </span>
        </div>

        <nav className="space-y-2">
          {navItems.map((item, index) => {
            // Color scheme: Green & Gold theme
            const baseColors = [
              'hover:bg-green-50 hover:text-green-700 hover:border-green-200',
              'hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200',
              'hover:bg-green-50 hover:text-green-700 hover:border-green-200',
              'hover:bg-green-50 hover:text-green-700 hover:border-green-200',
              'hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200',
              'hover:bg-green-50 hover:text-green-700 hover:border-green-200'
            ];
            const activeColors = [
              'bg-green-700 text-white border-green-800 shadow-lg',
              'bg-amber-500 text-white border-amber-600 shadow-lg',
              'bg-green-700 text-white border-green-800 shadow-lg',
              'bg-green-600 text-white border-green-700 shadow-lg',
              'bg-amber-500 text-white border-amber-600 shadow-lg',
              'bg-green-700 text-white border-green-800 shadow-lg'
            ];
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => soundEffects.playClick()}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) => 
                  `flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all border-2 border-transparent relative uppercase tracking-wide ${
                    isActive ? activeColors[index] : `text-stone-600 ${baseColors[index]}`
                  }`
                }
              >
                <item.icon className="w-5 h-5" style={{filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))'}} />
                {item.label}
                {item.showBadge && unreadMessages > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 bg-gradient-to-b from-amber-500 to-amber-600 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5 shadow-lg animate-pulse">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-stone-100 transition-colors" data-testid="user-menu">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-green-100 text-green-800">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-bold text-stone-900 truncate">{user?.name}</p>
                  <p className="text-xs text-stone-500 truncate">{user?.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-white">
              <DropdownMenuItem onClick={() => setShowEditProfile(true)} className="gap-2 cursor-pointer" data-testid="edit-profile-btn">
                <UserCircle className="w-4 h-4" />
                Edit Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/wallet')} className="gap-2 cursor-pointer" data-testid="golf-wallet-btn">
                <Wallet className="w-4 h-4" />
                Golf Wallet
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2 cursor-pointer" data-testid="account-settings-btn">
                <Settings className="w-4 h-4" />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/subscription')} className="gap-2 cursor-pointer">
                <CreditCard className="w-4 h-4" />
                Subscription
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600 cursor-pointer" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header - 3D Glass */}
      <header className="md:hidden sticky top-0 z-30 glass-3d p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/images/par-seeker-logo.png" 
              alt="Par Seeker" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-black tracking-tight text-2xl uppercase">
              <span className="text-green-700">Par</span> <span className="text-amber-500">Seeker</span>
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="w-10 h-10 cursor-pointer avatar-3d">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-green-100 text-green-800 text-sm font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white w-48">
              <DropdownMenuItem onClick={() => setShowEditProfile(true)} className="gap-2 cursor-pointer">
                <UserCircle className="w-4 h-4" />
                Edit Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/wallet')} className="gap-2 cursor-pointer">
                <Wallet className="w-4 h-4" />
                Golf Wallet
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2 cursor-pointer">
                <Settings className="w-4 h-4" />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/subscription')} className="gap-2 cursor-pointer">
                <CreditCard className="w-4 h-4" />
                Subscription
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600 cursor-pointer">
                <LogOut className="w-4 h-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Mobile Nav - Just under header - Icons only */}
        <nav className="flex justify-around mt-3 pt-3 border-t border-stone-200">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path || 
              (item.path === '/dashboard' && location.pathname === '/');
            // Colors: Green & Gold theme
            const colors = ['bg-green-700 text-white', 'bg-amber-500 text-white', 'bg-green-700 text-white', 'bg-amber-500 text-white', 'bg-green-700 text-white'];
            const activeColors = ['bg-green-800', 'bg-amber-600', 'bg-green-800', 'bg-amber-600', 'bg-green-800'];
            const colorClass = isActive ? `${colors[index]} ${activeColors[index]}` : 'bg-stone-50 text-stone-600';
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => soundEffects.playClick()}
                className={`relative flex items-center justify-center p-3 rounded-xl transition-all ${colorClass} ${isActive ? 'shadow-md scale-105' : 'hover:bg-stone-100'}`}
              >
                <item.icon className="w-6 h-6" />
                {item.showBadge && unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </header>

      {/* Main Content */}
      <main className="md:main-with-sidebar main-content p-4 md:p-8 pt-2">
        {children}
      </main>
    </div>
  );
};

/**
 * GameInviteManager - Global component that polls for pending game invites
 * Shows popup when user receives a game invitation
 */
const GameInviteManager = () => {
  const { user } = useAuth();
  const [currentInvite, setCurrentInvite] = useState(null);
  const [inviteQueue, setInviteQueue] = useState([]);
  const pollIntervalRef = useRef(null);
  
  // Poll for pending invites
  const fetchPendingInvites = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/group-rounds/pending-invites`);
      const { invites, count } = response.data;
      
      if (count > 0 && invites.length > 0) {
        // Add new invites to queue (avoid duplicates)
        setInviteQueue(prev => {
          const existingIds = new Set(prev.map(inv => inv.round_id));
          const newInvites = invites.filter(inv => !existingIds.has(inv.round_id));
          return [...prev, ...newInvites];
        });
      }
    } catch (error) {
      // Silent fail - don't interrupt UX for polling errors
      console.log('Invite poll failed:', error.message);
    }
  }, [user]);
  
  // Start polling when user logs in
  useEffect(() => {
    if (!user) {
      // Clear state on logout
      setCurrentInvite(null);
      setInviteQueue([]);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      return;
    }
    
    // Initial fetch
    fetchPendingInvites();
    
    // Poll every 10 seconds
    pollIntervalRef.current = setInterval(fetchPendingInvites, 10000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user, fetchPendingInvites]);
  
  // Show next invite from queue when current one is closed
  useEffect(() => {
    if (!currentInvite && inviteQueue.length > 0) {
      const [nextInvite, ...rest] = inviteQueue;
      setCurrentInvite(nextInvite);
      setInviteQueue(rest);
    }
  }, [currentInvite, inviteQueue]);
  
  const handleJoin = (invite) => {
    // Remove from queue and close popup
    setInviteQueue(prev => prev.filter(i => i.round_id !== invite.round_id));
    setCurrentInvite(null);
  };
  
  const handleDismiss = (invite) => {
    // Remove from queue and close popup
    setInviteQueue(prev => prev.filter(i => i.round_id !== invite.round_id));
    setCurrentInvite(null);
  };
  
  if (!currentInvite) return null;
  
  return (
    <GameInvitePopup
      invite={currentInvite}
      currentUserId={user?.user_id}
      onJoin={handleJoin}
      onDismiss={handleDismiss}
      onClose={() => setCurrentInvite(null)}
    />
  );
};

// Dark Theme Layout with new Navigation
const DarkDashboardLayout = ({ children }) => {
  const { user, logout, unreadMessages, profile, refreshProfile, subscription } = useAuth();
  const navigate = useNavigate();
  const [showCreatePost, setShowCreatePost] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Profile Setup Modal - First priority */}
      <ProfileSetupModal />
      <WalletSetupModal />
      
      {/* Onboarding Tour - Second priority (after profile setup) */}
      <OnboardingTour />
      
      {/* Login Subscribe Popup - Shows after onboarding during trial */}
      <LoginSubscribePopup />
      
      {/* Trial Expiry Popup - Shows when trial ends */}
      <TrialExpiryPopup />
      
      {/* Game Invite Popup - Shows when invited to a live round */}
      <GameInviteManager />
      
      {/* RonRo Help Button - Floating assistant */}
      <RonRoHelpButton user={user} />

      <DarkAppShell
        user={user}
        onLogout={handleLogout}
        unreadMessages={unreadMessages}
        onCreatePost={() => setShowCreatePost(true)}
      >
        {children}
      </DarkAppShell>
    </>
  );
};

// Info Button Component (for inline use)
const InfoButton = ({ infoType }) => {
  const [showInfo, setShowInfo] = useState(false);
  
  const pageInfo = {
    dashboard: {
      title: "Home Overview",
      icon: "📊",
      content: [
        "Welcome to your golf command center!",
        "",
        "• **Handicap Index** - Your calculated playing ability (needs 3+ rounds)",
        "• **Stats Cards** - Track rounds played, averages, best scores",
        "• **Score Trend** - Visual chart of your recent performance",
        "• **Recent Rounds** - Quick view of your latest games",
      ],
      encouragement: "Your journey to better golf starts here. Keep tracking, keep improving! 🏌️"
    },
    addScore: {
      title: "Add Score",
      icon: "➕",
      content: [
        "Log your rounds with ease!",
        "",
        "• **Select a Course** - Choose from your saved courses or add a new one",
        "• **Enter Your Score** - Front 9, Back 9, or total score",
        "• **Add Notes** - Remember key moments from your round",
        "",
        "Your scores build your handicap after 3 rounds."
      ],
      encouragement: "Every round logged is progress tracked. Let's capture this one! ⛳"
    },
    history: {
      title: "Score History",
      icon: "📜",
      content: [
        "Your complete golf journal!",
        "",
        "• **All Rounds** - Every score you've ever logged",
        "• **Sort & Filter** - Find specific rounds easily",
        "• **Score Details** - Tap any round for full breakdown",
        "• **Delete Rounds** - Remove incorrect entries",
      ],
      encouragement: "Look how far you've come. Every round tells a story! 📈"
    },
    stats: {
      title: "Statistics",
      icon: "📈",
      content: [
        "Deep dive into your performance!",
        "",
        "• **Scoring Averages** - Overall and by course",
        "• **Best/Worst Rounds** - Your range of play",
        "• **Trends Over Time** - See your improvement",
        "• **Course Stats** - Performance by location",
      ],
      encouragement: "Data reveals the path to improvement. Study, adapt, conquer! 🎯"
    },
    tournaments: {
      title: "Match Play Leagues",
      icon: "🏆",
      content: [
        "Compete with friends in team match play!",
        "",
        "• **Create Leagues** - Set up your own competition",
        "• **14-Point System** - 1 pt/hole + 5 pts for match win",
        "• **Team Format** - 2-person teams compete head-to-head",
        "• **Auto Scheduling** - Round-robin matchups generated",
        "• **Substitutes** - Add subs with 0 handicap anytime",
      ],
      encouragement: "Golf is better with friends. Let the competition begin! 🤝"
    },
    profile: {
      title: "Your Profile",
      icon: "👤",
      content: [
        "Manage your golf identity!",
        "",
        "• **Profile Info** - Name, username, photo",
        "• **Subscription** - Manage your plan",
        "• **Settings** - Customize your experience",
        "• **Account** - Security and preferences",
      ],
      encouragement: "Your profile, your golf journey. Make it yours! ⭐"
    }
  };

  const info = pageInfo[infoType];
  if (!info) return null;

  return (
    <>
      <button
        onClick={() => setShowInfo(true)}
        className="w-7 h-7 rounded-full bg-green-100 hover:bg-green-200 flex items-center justify-center text-green-700 hover:text-green-800 transition-colors"
        title={`About ${info.title}`}
      >
        <Info className="w-4 h-4" />
      </button>

      {showInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md animate-fade-in">
            <CardHeader className="text-center pb-2">
              <div className="text-4xl mb-2">{info.icon}</div>
              <CardTitle className="text-xl">{info.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-stone-600 space-y-2">
                {info.content.map((line, i) => (
                  <p key={`info-line-${i}`}>
                    <SafeTextWithBold text={line} />
                  </p>
                ))}
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium">{info.encouragement}</p>
              </div>
            </CardContent>
            <div className="px-6 pb-6">
              <Button onClick={() => setShowInfo(false)} className="w-full btn-primary">
                Got it, let's go!
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

// Page Header with Info Button Component
const PageHeader = ({ title, infoType }) => {
  return (
    <div className="relative mb-6">
      <div className="absolute top-0 right-0">
        <InfoButton infoType={infoType} />
      </div>
      <h1 className="text-2xl font-bold text-stone-900 font-manrope">{title}</h1>
    </div>
  );
};

// Protected Route with Subscription Check (allows 4 free rounds)
const SubscribedRoute = ({ children }) => {
  const { user, loading, subscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/');
    } else if (!loading && user && subscription && subscription.needs_subscription) {
      navigate('/subscription');
    }
  }, [user, loading, subscription, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="spinner" />
      </div>
    );
  }

  if (!user && !location.state?.user) return null;
  if (subscription && subscription.needs_subscription) return null;

  return children;
};

// Add Player Modal - Universal for Skins and Match Play
const AddPlayerModal = ({ eventId, eventType, onClose, onAdded }) => {
  const [saveError, setSaveError] = useState('');
  const savingRef = useRef(false);
  const addRequestRef = useRef(null);
  const [mode, setMode] = useState('search'); // 'search', 'ghost', 'email', 'message', 'sms'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestHandicap, setGuestHandicap] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  // SMS invite state
  const [smsPhone, setSmsPhone] = useState('');
  const [smsName, setSmsName] = useState('');
  const [smsCountryCode, setSmsCountryCode] = useState('+1');

  useEffect(() => {
    setSearchResults([]);
    if (searchQuery.trim().length < 2) { setSearching(false); return; }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/users/search?q=${encodeURIComponent(searchQuery.trim())}`, { signal: controller.signal, timeout: 15000 });
        if (!controller.signal.aborted) setSearchResults(Array.isArray(data.users) ? data.users.filter(u => u?.user_id && typeof u.name === 'string') : []);
      } catch (err) {
        if (!controller.signal.aborted) setSaveError(apiError(err, 'Unable to search players. Please retry.'));
      } finally { if (!controller.signal.aborted) setSearching(false); }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [searchQuery]);

  // Add Par Seeker user
  const addLinkedUser = async (userToAdd) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveError('');
    setLoading(true);
    try {
      const endpoint = eventType === 'skins' 
        ? `${API}/skins/events/${eventId}/invite`
        : eventType === 'stroke-play'
        ? `${API}/stroke-play/events/${eventId}/players`
        : `${API}/match-play/events/${eventId}/players`;
      
      if (eventType === 'skins') {
        await axios.post(endpoint, {
          type: 'user',
          user_id: userToAdd.user_id
        }, { timeout: 30000 });
      } else {
        await axios.post(endpoint, {
          user_id: userToAdd.user_id,
          name: userToAdd.name
        });
      }
      toast.success(`Added ${userToAdd.name}`);
      await onAdded();
    } catch (error) {
      setSaveError(apiError(error, 'Failed to add player. Please try again.'));
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  // Add ghost player
  const addGuestPlayer = async () => {
    if (savingRef.current) return;
    if (!guestName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    savingRef.current = true;
    setSaveError('');
    setLoading(true);
    try {
      const endpoint = eventType === 'skins'
        ? `${API}/skins/events/${eventId}/players`
        : eventType === 'stroke-play'
        ? `${API}/stroke-play/events/${eventId}/players`
        : `${API}/match-play/events/${eventId}/players`;
      
      const payload = {
        name: guestName.trim(),
        is_guest: true,
        handicap: parseOptionalHandicap(guestHandicap)
      };
      const signature = JSON.stringify(payload);
      if (addRequestRef.current?.signature !== signature) addRequestRef.current = { signature, id: crypto.randomUUID() };
      const response = await axios.post(endpoint, { ...payload, request_id: addRequestRef.current.id }, { timeout: 30000 });
      if (eventType === 'skins' && !response.data?.player_id) throw new Error('Missing saved player ID');
      toast.success(`Added ${guestName}${guestHandicap ? ` (HCP: ${guestHandicap})` : ''}`);
      setGuestName('');
      setGuestHandicap('');
      await onAdded();
    } catch (error) {
      setSaveError(apiError(error, 'Failed to add guest player. Please try again.'));
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  // Invite via email
  const sendEmailInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email");
      return;
    }
    setLoading(true);
    try {
      if (eventType === 'skins') {
        await axios.post(`${API}/skins/events/${eventId}/invite`, {
          type: 'email',
          email: inviteEmail.trim(),
          name: inviteName.trim() || inviteEmail.split('@')[0]
        });
      } else if (eventType === 'stroke-play') {
        await axios.post(`${API}/stroke-play/events/${eventId}/players`, {
          email: inviteEmail.trim(),
          name: inviteName.trim() || inviteEmail.split('@')[0],
          is_guest: true
        });
      } else {
        await axios.post(`${API}/skins/events/${eventId}/players`, {
          email: inviteEmail.trim(),
          name: inviteName.trim() || inviteEmail.split('@')[0],
          is_guest: true,
          invite_pending: true
        });
      }
      toast.success(eventType === 'skins' ? `Player added with email ${inviteEmail}. Email delivery is not enabled.` : `Player added: ${inviteEmail}`);
      await onAdded();
    } catch (error) {
      setSaveError(apiError(error, 'Failed to send invitation'));
    } finally {
      setLoading(false);
    }
  };

  // Send in-app message invite
  const sendMessageInvite = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/skins/events/${eventId}/invite`, {
        type: 'message',
        user_id: selectedUser.user_id
      });
      toast.success(`Message sent to ${selectedUser.name}`);
      // Also add them as a player
      await addLinkedUser(selectedUser);
    } catch (error) {
      toast.error("Failed to send message");
      setLoading(false);
    }
  };

  // Send SMS invite
  const sendSmsInvite = async () => {
    if (!smsPhone.trim()) {
      toast.error("Please enter a phone number");
      return;
    }
    if (!smsName.trim()) {
      toast.error("Please enter the player's name");
      return;
    }
    setLoading(true);
    try {
      const endpoint = eventType === 'skins'
        ? `${API}/skins/events/${eventId}/invite`
        : eventType === 'stroke-play'
        ? `${API}/stroke-play/events/${eventId}/players`
        : `${API}/match-play/events/${eventId}/players`;
      
      if (eventType === 'skins') {
        // Use the invite endpoint with SMS type
        const response = await axios.post(endpoint, {
          type: 'sms',
          phone_number: smsPhone.trim(),
          name: smsName.trim(),
          country_code: smsCountryCode
        });
        
        if (response.data.sms_sent) {
          toast.success(`SMS invitation sent to ${smsName}!`);
        } else {
          toast.success(`${smsName} added. SMS pending carrier approval.`);
        }
      } else {
        // Use the player endpoint with phone_number and send_sms flag
        const response = await axios.post(endpoint, {
          name: smsName.trim(),
          phone_number: smsCountryCode + smsPhone.trim().replace(/^\+/, ''),
          is_guest: true,
          send_sms: true,
          country_code: smsCountryCode
        });
        
        if (response.data.sms_sent) {
          toast.success(`SMS invitation sent to ${smsName}!`);
        } else {
          toast.success(`${smsName} added. SMS pending carrier approval.`);
        }
      }
      
      setSmsPhone('');
      setSmsName('');
      onAdded();
    } catch (error) {
      setSaveError(apiError(error, 'Failed to send SMS invitation'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open && !loading) onClose(); }}>
      <DialogContent hideClose aria-describedby={undefined} aria-busy={loading} data-testid="add-player-dialog"
        overlayClassName="z-[80]" className="ps-themed-dialog z-[81] w-[calc(100%_-_2rem)] max-w-lg max-h-[85dvh] overflow-y-auto p-0"
        onEscapeKeyDown={event => { event.stopPropagation(); if (savingRef.current) event.preventDefault(); }}
        onCloseAutoFocus={event => {
          const setup = eventType === 'skins' && document.getElementById('skins-setup-carousel');
          if (setup) { event.preventDefault(); setup.focus({ preventScroll: true }); }
        }}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-600" />
              Add Player
            </DialogTitle>
            <Button data-testid="add-player-close" aria-label="Close Add Player" disabled={loading} variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {saveError && <p role="alert" className="text-sm text-red-600" data-testid="add-player-error">{saveError}</p>}
          {loading && <p role="status" data-testid="add-player-saving" className="text-sm">Saving player…</p>}
          {/* Mode Tabs */}
          <div className="flex flex-wrap gap-1 p-1 bg-stone-100 rounded-lg">
            <button
              data-testid="add-player-search-tab" disabled={loading}
              onClick={() => setMode('search')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                mode === 'search' ? 'bg-white text-blue-600 shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <Search className="w-3 h-3" />
              Find User
            </button>
            <button
              data-testid="add-player-guest-tab" disabled={loading}
              onClick={() => setMode('ghost')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                mode === 'ghost' ? 'bg-white text-amber-600 shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <User className="w-3 h-3" />
              Guest
            </button>
            <button
              data-testid="add-player-email-tab" disabled={loading}
              onClick={() => setMode('email')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                mode === 'email' ? 'bg-white text-green-600 shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <Mail className="w-3 h-3" />
              Email
            </button>
            <button
              data-testid="add-player-message-tab" disabled={loading}
              onClick={() => setMode('message')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                mode === 'message' ? 'bg-white text-purple-600 shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <MessageCircle className="w-3 h-3" />
              Message
            </button>
            <button
              disabled={loading}
              onClick={() => setMode('sms')}
              className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                mode === 'sms' ? 'bg-white text-teal-600 shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
              data-testid="sms-invite-tab"
            >
              <Phone className="w-3 h-3" />
              SMS
            </button>
          </div>

          {/* Search Par Seeker Users */}
          {mode === 'search' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Search Par Seeker Users</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-user-input"
                  />
                </div>
              </div>
              
              {searching && (
                <div className="text-center py-4">
                  <div className="spinner w-6 h-6 border-blue-600 border-t-transparent mx-auto" />
                </div>
              )}
              
              {!searching && searchResults.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {searchResults.map((u) => (
                    <button type="button" disabled={loading} data-testid={`add-player-user-${u.user_id}`}
                      key={u.user_id}
                      className="w-full text-left flex items-center justify-between gap-2 p-3 bg-stone-50 rounded-lg hover:bg-blue-50 transition-colors break-all"
                      onClick={() => addLinkedUser(u)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserCircle className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{u.name}</p>
                          <p className="text-xs text-stone-500">{u.email}</p>
                        </div>
                      </div>
                      <PlusCircle className="w-5 h-5 text-blue-600" />
                    </button>
                  ))}
                </div>
              )}
              
              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="text-center py-4 text-stone-500">
                  <p className="text-sm">No users found</p>
                  <p className="text-xs mt-1">Try adding them as a guest or invite via email</p>
                </div>
              )}
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium">Linked Users</p>
                <p className="text-xs text-blue-600">Par Seeker users can view and submit scores in real-time</p>
              </div>
            </div>
          )}

          {/* Add Guest Player */}
          {mode === 'ghost' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Guest Player Name</label>
                <Input
                  placeholder="e.g., John Smith"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  data-testid="guest-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">
                  Handicap Index
                  <span className="text-stone-400 font-normal ml-1">(optional but recommended)</span>
                </label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="54"
                  placeholder="e.g., 12.5"
                  value={guestHandicap}
                  onChange={(e) => setGuestHandicap(e.target.value)}
                  data-testid="guest-handicap-input"
                />
                <p className="text-xs text-stone-500 mt-1">Used for net score calculations in handicap games</p>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <p className="font-medium">Guest Players</p>
                <p className="text-xs text-amber-600">For players without Par Seeker accounts. You'll enter their scores manually.</p>
              </div>
              
              <Button 
                data-testid="add-player-guest-submit"
                onClick={addGuestPlayer} 
                disabled={loading || !guestName.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Add Guest Player'}
              </Button>
            </div>
          )}

          {/* Invite via Email */}
          {mode === 'email' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Email Address</label>
                <Input
                  type="email"
                  placeholder="friend@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Name (optional)</label>
                <Input
                  data-testid="invite-name-input"
                  placeholder="Their name"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                <p className="font-medium">Email Invitation</p>
                <p className="text-xs text-green-600" data-testid="add-player-email-status">{eventType === 'skins' ? 'Email delivery is not enabled. The player will be saved with this email address.' : 'Add the player with their email address.'}</p>
              </div>
              
              <Button 
                data-testid="add-player-email-submit"
                onClick={sendEmailInvite} 
                disabled={loading || !inviteEmail.trim()}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Add Player with Email'}
              </Button>
            </div>
          )}

          {/* Invite via In-App Message */}
          {mode === 'message' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Search User to Message</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    data-testid="add-player-message-search"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {searching && (
                <div className="text-center py-4">
                  <div className="spinner w-6 h-6 border-purple-600 border-t-transparent mx-auto" />
                </div>
              )}
              
              {!searching && searchResults.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {searchResults.map((u) => (
                    <div
                      data-testid={`add-player-message-user-${u.user_id}`}
                      key={u.user_id}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedUser?.user_id === u.user_id ? 'bg-purple-100 border-2 border-purple-300' : 'bg-stone-50 hover:bg-purple-50'
                      }`}
                      onClick={() => setSelectedUser(u)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <UserCircle className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{u.name}</p>
                          <p className="text-xs text-stone-500">{u.email}</p>
                        </div>
                      </div>
                      {selectedUser?.user_id === u.user_id && (
                        <CheckCircle className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                <p className="font-medium">In-App Invitation</p>
                <p className="text-xs text-purple-600">Send a message through Golf Connect inviting them to play</p>
              </div>
              
              <Button 
                data-testid="add-player-message-submit"
                onClick={sendMessageInvite} 
                disabled={loading || !selectedUser}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Send Message & Add Player'}
              </Button>
            </div>
          )}

          {/* SMS Invite */}
          {mode === 'sms' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Player Name</label>
                <Input
                  placeholder="e.g., John Smith"
                  value={smsName}
                  onChange={(e) => setSmsName(e.target.value)}
                  data-testid="sms-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Phone Number</label>
                <div className="flex gap-2">
                  <select
                    data-testid="add-player-sms-country"
                    value={smsCountryCode}
                    onChange={(e) => setSmsCountryCode(e.target.value)}
                    className="px-3 py-2 border rounded-lg bg-white text-sm w-20"
                  >
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+61">+61</option>
                    <option value="+91">+91</option>
                  </select>
                  <Input
                    type="tel"
                    placeholder="555-123-4567"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value.replace(/[^0-9]/g, ''))}
                    className="flex-1"
                    data-testid="sms-phone-input"
                  />
                </div>
              </div>
              
              <div className="p-3 bg-teal-50 rounded-lg text-sm text-teal-700">
                <p className="font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  SMS Invitation
                </p>
                <p className="text-xs text-teal-600 mt-1">
                  They'll receive a text: "{smsName || 'Name'} is inviting you to view their game on Par Seeker!"
                </p>
              </div>
              
              <Button 
                onClick={sendSmsInvite} 
                disabled={loading || !smsPhone.trim() || !smsName.trim()}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="send-sms-btn"
              >
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Send SMS Invitation'}
              </Button>
              
              <p className="text-xs text-stone-400 text-center">
                SMS delivery requires carrier approval. Player will be added regardless.
              </p>
            </div>
          )}
        </CardContent>
      </DialogContent>
    </Dialog>
  );
};

const AddPlayersModal = ({ eventId, onClose, onAdded }) => {
  const [names, setNames] = useState('');
  const [emails, setEmails] = useState('');
  const [handicaps, setHandicaps] = useState(''); // NEW: Handicap field for Match Play
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('names'); // 'names', 'search', or 'substitute'
  const [subName, setSubName] = useState('');
  const [subEmail, setSubEmail] = useState('');
  const [subHandicap, setSubHandicap] = useState(''); // NEW: Handicap for substitutes

  const handleAddByName = async () => {
    const nameList = names.split(/[,\n]/).map(n => n.trim()).filter(n => n);
    const emailList = emails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    const handicapList = handicaps.split(/[,\n]/).map(h => h.trim());
    if (nameList.length === 0) {
      toast.error("Please enter at least one name");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/match-play/events/${eventId}/players`, { 
        names: nameList,
        emails: emailList,
        handicaps: handicapList.map(parseOptionalHandicap)
      });
      setResults(response.data);
      if (response.data.added?.length > 0) {
        toast.success(`Added ${response.data.added.length} player(s)`);
        setNames('');
        setEmails('');
        setHandicaps('');
        onAdded();
      }
    } catch (error) {
      toast.error("Failed to add players");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubstitute = async () => {
    if (!subName.trim()) {
      toast.error("Please enter a name for the substitute");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/match-play/events/${eventId}/players`, { 
        names: [subName.trim()],
        emails: subEmail.trim() ? [subEmail.trim()] : [],
        handicaps: [parseOptionalHandicap(subHandicap)],
        is_substitute: true
      });
      if (response.data.added?.length > 0) {
        const hcpDisplay = subHandicap ? subHandicap : '0';
        toast.success(`Added substitute: ${subName.trim()} (HCP: ${hcpDisplay})`);
        setSubName('');
        setSubEmail('');
        setSubHandicap('');
        onAdded();
      }
    } catch (error) {
      toast.error("Failed to add substitute");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/search-users?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const handleLinkUser = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/match-play/events/${eventId}/players`, { user_ids: [userId] });
      if (response.data.added?.length > 0) {
        toast.success(`Linked ${response.data.added[0].name}`);
        setSearchResults(searchResults.map(u => u.user_id === userId ? {...u, already_in_event: true} : u));
        onAdded();
      }
    } catch (error) {
      toast.error("Failed to link user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Add Players</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tabs */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={activeTab === 'names' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('names')}
              className={activeTab === 'names' ? 'btn-primary' : ''}
            >
              Add by Name
            </Button>
            <Button
              variant={activeTab === 'substitute' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('substitute')}
              className={activeTab === 'substitute' ? 'btn-primary' : ''}
            >
              Add Substitute
            </Button>
            <Button
              variant={activeTab === 'search' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('search')}
              className={activeTab === 'search' ? 'btn-primary' : ''}
            >
              Link Existing User
            </Button>
          </div>

          {/* Substitute Tab */}
          {activeTab === 'substitute' && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 font-medium">Substitute Player</p>
                <p className="text-xs text-amber-600 mt-1">Add a substitute with their handicap for match play</p>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Substitute Name</label>
                <Input
                  placeholder="Enter substitute player name..."
                  value={subName}
                  onChange={(e) => setSubName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Handicap</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="54"
                    placeholder="e.g., 12.5"
                    value={subHandicap}
                    onChange={(e) => setSubHandicap(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Email (optional)</label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={subEmail}
                    onChange={(e) => setSubEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSubstitute()}
                  />
                </div>
              </div>
              <Button onClick={handleAddSubstitute} disabled={loading} className="w-full btn-primary">
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : `Add Substitute${subHandicap ? ` (HCP: ${subHandicap})` : ''}`}
              </Button>
            </>
          )}

          {activeTab === 'names' ? (
            <>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Player Names</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  placeholder="Enter names separated by commas or new lines...&#10;John Smith&#10;Jane Doe"
                  value={names}
                  onChange={(e) => setNames(e.target.value)}
                />
                <p className="text-xs text-stone-500 mt-1">Players don't need a Par Seeker account</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Handicaps (optional, same order as names)</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="Enter handicaps separated by commas or new lines...&#10;12.5&#10;8.0"
                  value={handicaps}
                  onChange={(e) => setHandicaps(e.target.value)}
                />
                <p className="text-xs text-stone-500 mt-1">For net score calculations</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Emails (optional, same order as names)</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="Enter emails separated by commas or new lines...&#10;john@email.com&#10;jane@email.com"
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                />
                <p className="text-xs text-stone-500 mt-1">For weekly score updates</p>
              </div>

              {results && results.added?.length > 0 && (
                <div className="text-sm text-green-700">
                  ✓ Added: {results.added.map(p => p.name).join(', ')}
                </div>
              )}

              <Button onClick={handleAddByName} disabled={loading} className="w-full btn-primary">
                {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Add Players'}
              </Button>
            </>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Search Par Seeker Users</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by name, username, or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch} disabled={searching} variant="outline">
                    {searching ? <div className="spinner w-4 h-4 border-green-600 border-t-transparent" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-stone-500 mt-1">Link to pull their profile handicap (optional)</p>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {searchResults.map((user) => (
                    <div key={user.user_id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <span className="text-green-700 font-medium text-sm">{user.name?.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{user.name}</p>
                          {user.username && <p className="text-xs text-stone-500">@{user.username}</p>}
                        </div>
                      </div>
                      {user.already_in_event ? (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Added</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleLinkUser(user.user_id)} disabled={loading}>
                          Add
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
        <div className="px-6 pb-6">
          <Button variant="outline" onClick={onClose} className="w-full">Done</Button>
        </div>
      </Card>
    </div>
  );
};

// Create Team Modal
const CreateTeamModal = ({ eventId, players, teams, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get unassigned players (use player_id for both registered and guest)
  const assignedPlayerIds = new Set(teams.flatMap(t => t.player_ids));
  const availablePlayers = players.filter(p => !assignedPlayerIds.has(p.player_id));

  const togglePlayer = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
    } else if (selectedPlayers.length < 2) {
      setSelectedPlayers([...selectedPlayers, playerId]);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a team name");
      return;
    }
    if (selectedPlayers.length !== 2) {
      toast.error("Please select exactly 2 players");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/match-play/events/${eventId}/teams`, {
        name: name.trim(),
        player_ids: selectedPlayers
      });
      toast.success("Team created!");
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Create Team</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 block">Team Name</label>
            <Input placeholder="e.g., The Eagles" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700 mb-2 block">Select 2 Players</label>
            {availablePlayers.length === 0 ? (
              <p className="text-sm text-amber-600">No unassigned players available. Add more players first.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availablePlayers.map((player) => (
                  <div
                    key={player.player_id}
                    onClick={() => togglePlayer(player.player_id)}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPlayers.includes(player.player_id) ? 'border-green-500 bg-green-50' : 'hover:border-stone-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedPlayers.includes(player.player_id) ? 'border-green-500 bg-green-500' : 'border-stone-300'
                    }`}>
                      {selectedPlayers.includes(player.player_id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{player.name}</span>
                      {player.type === 'guest' && (
                        <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">Guest</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleCreate} disabled={loading || selectedPlayers.length !== 2} className="flex-1 btn-primary">
            {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Create Team'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Add Score Modal with Photo Scanner
const AddScoreModal = ({ eventId, weekNumber, players, onClose, onAdded }) => {
  const { user } = useAuth();
  const [selectedPlayer, setSelectedPlayer] = useState(user?.id || '');
  const [scoreType, setScoreType] = useState('hole_by_hole');
  const [holeScores, setHoleScores] = useState(['', '', '', '', '', '', '', '', '']);
  const [totalScore, setTotalScore] = useState('');
  const [courseName, setCourseName] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Photo scanner states
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleHoleChange = (index, value) => {
    const newScores = [...holeScores];
    newScores[index] = value;
    setHoleScores(newScores);
  };

  const calculateTotal = () => {
    return holeScores.reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  };

  // Photo scanner handlers
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(selectedFile);
      setScanResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(droppedFile);
      setScanResult(null);
    }
  };

  const analyzeScorecard = async () => {
    if (!file) {
      toast.error("Please select a scorecard image first");
      return;
    }

    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/scorecard/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setScanResult(response.data);
        // Extract 9 hole scores (front 9 or first 9)
        const holes = response.data.holes || [];
        const scores = holes.slice(0, 9).map(h => h.score?.toString() || '');
        // Pad with empty strings if less than 9 holes
        while (scores.length < 9) scores.push('');
        setHoleScores(scores);
        if (response.data.course_name) {
          setCourseName(response.data.course_name);
        }
        toast.success("Scorecard scanned! Review the scores below.");
      } else {
        toast.error(response.data.message || "Could not analyze scorecard");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze scorecard");
    } finally {
      setAnalyzing(false);
    }
  };

  const clearPhoto = () => {
    setFile(null);
    setPreview(null);
    setScanResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!selectedPlayer) {
      toast.error("Please select a player");
      return;
    }

    let payload = {
      player_id: selectedPlayer,
      week_number: weekNumber,
      score_type: scoreType === 'photo' ? 'photo' : scoreType,
      course_name: courseName || null,
      date: new Date().toISOString().split('T')[0]
    };

    if (scoreType === 'hole_by_hole' || scoreType === 'photo') {
      const scores = holeScores.map(s => parseInt(s) || 0);
      if (scores.some(s => s < 1 || s > 15)) {
        toast.error("Please enter valid scores for all 9 holes (1-15)");
        return;
      }
      payload.hole_scores = scores;
    } else {
      const total = parseInt(totalScore);
      if (!total || total < 20 || total > 80) {
        toast.error("Please enter a valid total score (20-80)");
        return;
      }
      payload.total_score = total;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/match-play/events/${eventId}/rounds`, payload);
      toast.success("Score added!");
      onAdded();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add score");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-xl animate-fade-in my-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Add Score - Week {weekNumber}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
          <p className="text-xs text-stone-500">14 points per match: 9 hole points + 5 match points</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 block">Player</label>
            <select
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              value={selectedPlayer}
              onChange={(e) => setSelectedPlayer(e.target.value)}
            >
              <option value="">Select player...</option>
              {players.map((p) => (
                <option key={p.player_id} value={p.player_id}>{p.name} {p.type === 'guest' ? '(Guest)' : ''}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 block">Entry Type</label>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                variant={scoreType === 'photo' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScoreType('photo')}
                className={scoreType === 'photo' ? 'btn-primary' : ''}
              >
                <Camera className="w-4 h-4 mr-1" />
                Photo Scan
              </Button>
              <Button
                type="button"
                variant={scoreType === 'hole_by_hole' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScoreType('hole_by_hole')}
                className={scoreType === 'hole_by_hole' ? 'btn-primary' : ''}
              >
                <Edit className="w-4 h-4 mr-1" />
                Manual Entry
              </Button>
              <Button
                type="button"
                variant={scoreType === 'final_only' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScoreType('final_only')}
                className={scoreType === 'final_only' ? 'btn-primary' : ''}
              >
                <Hash className="w-4 h-4 mr-1" />
                Total Only
              </Button>
            </div>
          </div>

          {/* Photo Scanner Section */}
          {scoreType === 'photo' && (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {!preview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
                >
                  <Camera className="w-10 h-10 text-stone-400 mx-auto mb-2" />
                  <p className="text-stone-600 font-medium mb-1">Take photo or upload scorecard</p>
                  <p className="text-xs text-stone-400">AI will extract your hole-by-hole scores</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <img src={preview} alt="Scorecard" className="w-full rounded-lg border max-h-48 object-contain bg-stone-50" />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={clearPhoto}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {!scanResult && (
                    <Button 
                      onClick={analyzeScorecard} 
                      disabled={analyzing}
                      className="w-full btn-primary"
                    >
                      {analyzing ? (
                        <>
                          <div className="spinner w-4 h-4 border-white border-t-transparent mr-2" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-2" />
                          Scan Scorecard
                        </>
                      )}
                    </Button>
                  )}
                  
                  {scanResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                      <Check className="w-4 h-4 inline mr-1" />
                      Scorecard scanned! Review and edit scores below.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Hole-by-hole scores (shown for photo and manual entry) */}
          {(scoreType === 'hole_by_hole' || scoreType === 'photo') && (
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">
                Hole Scores {scoreType === 'photo' && scanResult && '(from scan)'}
              </label>
              <div className="grid grid-cols-9 gap-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((hole, idx) => (
                  <div key={hole} className="text-center">
                    <div className="text-xs text-stone-500 mb-1">{hole}</div>
                    <Input
                      type="number"
                      min={1}
                      max={15}
                      className="text-center p-1 h-10"
                      value={holeScores[idx]}
                      onChange={(e) => handleHoleChange(idx, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t">
                <span className="text-sm font-medium text-stone-700">Total:</span>
                <span className="text-xl font-bold text-green-700">{calculateTotal() || '-'}</span>
              </div>
            </div>
          )}

          {/* Final score only */}
          {scoreType === 'final_only' && (
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Total 9-Hole Score</label>
              <Input
                type="number"
                min={20}
                max={80}
                placeholder="e.g., 42"
                value={totalScore}
                onChange={(e) => setTotalScore(e.target.value)}
              />
              <p className="text-xs text-stone-500 mt-1">Note: Hole-by-hole entry required for full point calculation</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 block">Course (optional)</label>
            <Input placeholder="e.g., Pebble Beach" value={courseName} onChange={(e) => setCourseName(e.target.value)} />
          </div>
        </CardContent>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || (scoreType === 'photo' && !scanResult && !holeScores.some(s => s))} className="flex-1 btn-primary">
            {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Save Score'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Set Matchups Modal - Enhanced with editing
const SetMatchupsModal = ({ eventId, weekNumber, teams, existingMatchups, onClose, onSaved }) => {
  const [matchups, setMatchups] = useState([{ team1_id: '', team2_id: '' }]);
  const [loading, setLoading] = useState(false);

  // Load existing matchups on mount
  useEffect(() => {
    if (existingMatchups && existingMatchups.length > 0) {
      setMatchups(existingMatchups.map(m => ({ team1_id: m.team1_id, team2_id: m.team2_id })));
    }
  }, [existingMatchups]);

  const addMatchup = () => {
    setMatchups([...matchups, { team1_id: '', team2_id: '' }]);
  };

  const updateMatchup = (index, field, value) => {
    const newMatchups = [...matchups];
    newMatchups[index][field] = value;
    setMatchups(newMatchups);
  };

  const removeMatchup = (index) => {
    setMatchups(matchups.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const validMatchups = matchups.filter(m => m.team1_id && m.team2_id && m.team1_id !== m.team2_id);
    if (validMatchups.length === 0) {
      toast.error("Please add at least one valid matchup");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/match-play/events/${eventId}/matchups`, {
        week_number: weekNumber,
        matches: validMatchups
      });
      toast.success("Matchups saved!");
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save matchups");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{existingMatchups?.length > 0 ? 'Edit' : 'Set'} Matchups - Week {weekNumber}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {matchups.map((matchup, index) => (
            <div key={matchup.team1_id ? `matchup-${matchup.team1_id}-${matchup.team2_id}` : `matchup-new-${index}`} className="flex items-center gap-2">
              <select
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={matchup.team1_id}
                onChange={(e) => updateMatchup(index, 'team1_id', e.target.value)}
              >
                <option value="">Team 1...</option>
                {teams.map((t) => (
                  <option key={t.team_id} value={t.team_id}>{t.name}</option>
                ))}
              </select>
              <span className="text-stone-400 font-medium">vs</span>
              <select
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={matchup.team2_id}
                onChange={(e) => updateMatchup(index, 'team2_id', e.target.value)}
              >
                <option value="">Team 2...</option>
                {teams.map((t) => (
                  <option key={t.team_id} value={t.team_id}>{t.name}</option>
                ))}
              </select>
              {matchups.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeMatchup(index)}>
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
          
          <Button variant="outline" onClick={addMatchup} className="w-full">
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Another Matchup
          </Button>
        </CardContent>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSave} disabled={loading} className="flex-1 btn-primary">
            {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Save Matchups'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Schedule Manager Modal - Generate or manage full season schedule
const ScheduleManagerModal = ({ eventId, totalWeeks, teams, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showAddWeeks, setShowAddWeeks] = useState(false);
  const [additionalWeeks, setAdditionalWeeks] = useState(4);
  const [addingWeeks, setAddingWeeks] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [subName, setSubName] = useState('');
  const [addingSub, setAddingSub] = useState(false);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/match-play/events/${eventId}/schedule`);
      setSchedule(response.data.schedule || []);
    } catch (error) {
      console.error("Error fetching schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]); // fetchSchedule uses eventId internally

  const toggleWeek = (week) => {
    if (selectedWeeks.includes(week)) {
      setSelectedWeeks(selectedWeeks.filter(w => w !== week));
    } else {
      setSelectedWeeks([...selectedWeeks, week]);
    }
  };

  const selectAllEmpty = () => {
    const emptyWeeks = schedule.filter(s => !s.has_matchups).map(s => s.week);
    setSelectedWeeks(emptyWeeks);
  };

  const selectAll = () => {
    setSelectedWeeks(schedule.map(s => s.week));
  };

  const handleGenerateSchedule = async () => {
    if (selectedWeeks.length === 0) {
      toast.error("Please select weeks to generate matchups for");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/match-play/events/${eventId}/generate-schedule`, {
        weeks: selectedWeeks
      });
      toast.success(response.data.message);
      setSelectedWeeks([]);
      fetchSchedule();
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate schedule");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteWeek = async (week) => {
    if (!confirm(`Delete matchups for Week ${week}?`)) return;
    
    try {
      await axios.delete(`${API}/match-play/events/${eventId}/matchups/${week}`);
      toast.success(`Week ${week} matchups deleted`);
      fetchSchedule();
      onSaved();
    } catch (error) {
      toast.error("Failed to delete matchups");
    }
  };

  const handleAddWeeks = async () => {
    if (additionalWeeks < 1 || additionalWeeks > 18) {
      toast.error("Please enter 1-18 weeks");
      return;
    }

    setAddingWeeks(true);
    try {
      const newTotal = schedule.length + additionalWeeks;
      await axios.put(`${API}/match-play/events/${eventId}`, {
        name: "", // Will use existing
        description: "",
        total_weeks: newTotal,
        max_players: 50
      });
      toast.success(`Added ${additionalWeeks} weeks (now ${newTotal} total)`);
      setShowAddWeeks(false);
      setAdditionalWeeks(4);
      fetchSchedule();
      onSaved();
    } catch (error) {
      toast.error("Failed to add weeks");
    } finally {
      setAddingWeeks(false);
    }
  };

  const handleAddSubstitute = async () => {
    if (!subName.trim()) {
      toast.error("Please enter substitute name");
      return;
    }

    setAddingSub(true);
    try {
      await axios.post(`${API}/match-play/events/${eventId}/players`, {
        names: [subName.trim()],
        is_substitute: true
      });
      toast.success(`Added substitute: ${subName.trim()} (HCP: 0)`);
      setSubName('');
      setShowAddSub(false);
      onSaved();
    } catch (error) {
      toast.error("Failed to add substitute");
    } finally {
      setAddingSub(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl animate-fade-in my-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Schedule Manager</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
          <p className="text-sm text-stone-500">Generate or manually set weekly matchups</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowAddWeeks(!showAddWeeks)}
              className={showAddWeeks ? 'border-green-500' : ''}
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              Add Weeks
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowAddSub(!showAddSub)}
              className={showAddSub ? 'border-green-500' : ''}
            >
              <User className="w-4 h-4 mr-1" />
              Add Substitute
            </Button>
          </div>

          {/* Add Weeks Panel */}
          {showAddWeeks && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Add More Weeks</h4>
              <p className="text-sm text-blue-600 mb-3">Extend the season by adding additional weeks</p>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  min={1}
                  max={18}
                  value={additionalWeeks}
                  onChange={(e) => setAdditionalWeeks(parseInt(e.target.value) || 1)}
                  className="w-24"
                />
                <span className="text-sm text-stone-600">weeks to add</span>
                <Button 
                  onClick={handleAddWeeks} 
                  disabled={addingWeeks}
                  size="sm"
                  className="btn-primary ml-auto"
                >
                  {addingWeeks ? 'Adding...' : `Add (→ ${schedule.length + additionalWeeks} total)`}
                </Button>
              </div>
            </div>
          )}

          {/* Add Substitute Panel */}
          {showAddSub && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 mb-2">Add Substitute Player</h4>
              <p className="text-sm text-amber-600 mb-3">Substitutes always play with 0 handicap</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Substitute name..."
                  value={subName}
                  onChange={(e) => setSubName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSubstitute()}
                />
                <Button 
                  onClick={handleAddSubstitute} 
                  disabled={addingSub}
                  size="sm"
                  className="btn-primary"
                >
                  {addingSub ? 'Adding...' : 'Add Sub'}
                </Button>
              </div>
            </div>
          )}

          {/* Auto-generate section */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">Auto-Generate Matchups</h4>
            <p className="text-sm text-green-700 mb-3">Select weeks and generate round-robin matchups automatically</p>
            <div className="flex gap-2 mb-3 flex-wrap">
              <Button size="sm" variant="outline" onClick={selectAllEmpty}>Select Empty</Button>
              <Button size="sm" variant="outline" onClick={selectAll}>Select All</Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedWeeks([])}>Clear</Button>
            </div>
            <Button 
              onClick={handleGenerateSchedule} 
              disabled={generating || selectedWeeks.length === 0}
              className="btn-primary"
            >
              {generating ? (
                <>
                  <div className="spinner w-4 h-4 border-white border-t-transparent mr-2" />
                  Generating...
                </>
              ) : (
                <>Generate {selectedWeeks.length} Week{selectedWeeks.length !== 1 ? 's' : ''}</>
              )}
            </Button>
          </div>

          {/* Schedule overview */}
          <div>
            <h4 className="font-medium text-stone-800 mb-2">Season Schedule ({schedule.length} weeks)</h4>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="spinner w-6 h-6 border-green-600 border-t-transparent" />
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-2">
                {schedule.map((week) => (
                  <div 
                    key={week.week}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedWeeks.includes(week.week) 
                        ? 'border-green-500 bg-green-50' 
                        : week.has_matchups 
                          ? 'bg-white' 
                          : 'bg-stone-50'
                    }`}
                    onClick={() => toggleWeek(week.week)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedWeeks.includes(week.week) ? 'border-green-500 bg-green-500' : 'border-stone-300'
                      }`}>
                        {selectedWeeks.includes(week.week) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-medium">Week {week.week}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {week.has_matchups ? (
                        <>
                          <span className="text-sm text-stone-600">
                            {week.matches.length} match{week.matches.length !== 1 ? 'es' : ''}
                          </span>
                          <div className="flex gap-1">
                            {week.matches.some(m => m.status === 'completed') && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Results</span>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={(e) => { e.stopPropagation(); handleDeleteWeek(week.week); }}
                              className="text-red-500 hover:text-red-700 p-1 h-auto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-stone-400">No matchups set</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
        <div className="px-6 pb-6">
          <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
        </div>
      </Card>
    </div>
  );
};

// Wrapper for FindGolfersPage to pass auth context
const FindGolfersPageWrapper = () => {
  const { subscription, user, fetchUnreadCount, playMessageChime } = useAuth();
  return <FindGolfersPage subscription={subscription} user={user} fetchUnreadCount={fetchUnreadCount} playMessageChime={playMessageChime} />;
};

// Wrapper for FindGolfersHub (new utility-focused golfer search)
const FindGolfersHubWrapper = () => {
  const { user } = useAuth();
  return <FindGolfersHub user={user} />;
};

// Wrapper for SocialFeedPage (new dark theme home)
const SocialFeedPageWrapper = () => {
  const { subscription, user } = useAuth();
  return <SocialFeedPage subscription={subscription} user={user} />;
};

// Wrapper for DashboardPage to pass auth context
// Shows NewUserDashboard for Phase 1 users (< 3 rounds), regular dashboard otherwise
const DashboardPageWrapper = () => {
  const { user, refreshProfile, profile, showOnboarding } = useAuth();
  const [milestones, setMilestones] = useState(null);
  const [loadingMilestones, setLoadingMilestones] = useState(true);

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/milestones/status`);
        setMilestones(response.data);
      } catch (error) {
        console.error("Error fetching milestones:", error);
        // Default to full dashboard on error
        setMilestones({ phase: 4 });
      } finally {
        setLoadingMilestones(false);
      }
    };

    if (user) {
      fetchMilestones();
    } else {
      setLoadingMilestones(false);
    }
  }, [user]);

  // Show loading while checking milestones
  if (loadingMilestones) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--ps-bg-base)]">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show NewUserDashboard for Phase 1 users (haven't reached 3 rounds yet)
  if (milestones && milestones.phase === 1) {
    return <NewUserDashboard user={user} />;
  }

  // Show PremiumDashboard for all users - monetization focused
  return <PremiumDashboard user={user} />;
};

// Wrapper for AddScorePage to pass auth context
const AddScorePageWrapper = () => {
  const { refreshSubscription, user, profile, refreshProfile } = useAuth();
  // Merge user and profile data so handicap is available
  const currentUser = { ...user, handicap: profile?.handicap };
  return <AddScorePage refreshSubscription={refreshSubscription} user={currentUser} refreshProfile={refreshProfile} />;
};

// Wrapper for HistoryPage with user context
const HistoryPageWrapper = () => {
  const { user, profile } = useAuth();
  // Merge user and profile data so handicap is available
  const currentUser = { ...user, handicap: profile?.handicap };
  return <HistoryPage currentUser={currentUser} />;
};

// Wrapper for MatchPlayPage to pass user context
const MatchPlayPageWrapper = () => {
  const { user } = useAuth();
  return <MatchPlayPage user={user} />;
};

// Wrapper for SkinsPage to pass user context
const SkinsPageWrapper = () => {
  const { user } = useAuth();
  return <SkinsPage user={user} />;
};

// Wrapper for StrokePlayPage to pass user context
const StrokePlayPageWrapper = () => {
  const { user } = useAuth();
  return <StrokePlayPage user={user} />;
};

// Wrapper for TournamentsPage to pass user context
const TournamentsPageWrapper = () => {
  const { user } = useAuth();
  return <TournamentsPage currentUser={user} />;
};

// Wrapper for MySettlementsPage to pass user context
const MySettlementsPageWrapper = () => {
  const { user } = useAuth();
  return <MySettlementsPage currentUserId={user?.user_id} />;
};

// Wrapper for SubscriptionPage to pass subscription context
const SubscriptionPageWrapper = () => {
  const { subscription, refreshSubscription } = useAuth();
  return <SubscriptionPage subscription={subscription} refreshSubscription={refreshSubscription} />;
};

// Wrapper for SubscriptionSuccessPage to pass refresh function
const SubscriptionSuccessPageWrapper = () => {
  const { refreshSubscription } = useAuth();
  return <SubscriptionSuccessPage refreshSubscription={refreshSubscription} />;
};

const SettingsPageWrapper = () => {
  const { user, profile } = useAuth();
  return <SettingsPage currentUser={user} profile={profile} />;
};

const EmailTemplatesPageWrapper = () => {
  const { user } = useAuth();
  return <EmailTemplatesPage user={user} />;
};

// Hook Feed wrapper
const HookFeedWrapper = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  return <HookFeed onBack={() => navigate(-1)} user={user} />;
};

// Wrapper for BookTeeTimePage to pass auth context
const BookTeeTimePageWrapper = () => {
  const { user } = useAuth();
  return <BookTeeTimePage user={user} />;
};

// Events page wrappers
const EventsPageWrapper = () => {
  const { user } = useAuth();
  return <EventsPage user={user} />;
};

const EventDetailPageWrapper = () => {
  const { user } = useAuth();
  return <EventDetailPage user={user} />;
};

// Groups page wrappers
const GroupsPageWrapper = () => {
  const { user } = useAuth();
  return <GroupsPage user={user} />;
};

const GroupDetailPageWrapper = () => {
  const { user } = useAuth();
  return <GroupDetailPage user={user} />;
};

// Friends page wrapper
const FriendsPageWrapper = () => {
  const { user } = useAuth();
  return <FriendsPage user={user} />;
};

// Chat page wrapper
const ChatPageWrapper = () => {
  const { user } = useAuth();
  return <ChatPage user={user} />;
};

// Buy/Sell page wrapper
const BuySellPageWrapper = () => {
  const { user } = useAuth();
  return <BuySellPage user={user} />;
};

// Home Preview page wrapper (Pilot preview of new home layout)
const HomePreviewWrapper = () => {
  const { user, subscription } = useAuth();
  return <HomePreview user={user} subscription={subscription} />;
};

// AI Tools page wrapper
const AIToolsPageWrapper = () => {
  const { user } = useAuth();
  return <AIToolsPage user={user} />;
};

// JoinGame page wrapper
const JoinGamePageWrapper = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const handleShowAuth = () => {
    // Root now opens the login form directly; preserve existing invite links.
    navigate('/?auth=login');
  };
  
  return <JoinGamePage user={user} onShowAuth={handleShowAuth} />;
};

// Profile page wrapper
const ProfilePageWrapper = () => {
  const { user } = useAuth();
  return <ProfilePage user={user} />;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const { user, loading, showProfileSetup, showWalletSetup } = useAuth();
  useEventReturn(user, loading, !showProfileSetup && !showWalletSetup);
  
  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/proshop" element={<LoginPage proshop />} />
      <Route path="/proshop/dashboard/*" element={<ProtectedRoute><DarkDashboardLayout><CourseBusinessDashboard /></DarkDashboardLayout></ProtectedRoute>} />
      <Route path="/top10" element={<ProtectedRoute><DarkDashboardLayout><Top10Page /></DarkDashboardLayout></ProtectedRoute>} />
      <Route path="/proshop/admin/workspace/:workspaceId/*" element={<CourseBusinessDashboard admin />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/tournament-rules" element={<TournamentRulesPage />} />
      <Route path="/investors" element={<InvestorPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding/wallet" element={<ProtectedRoute><WalletOnboardingReturnPage /></ProtectedRoute>} />
      <Route path="/invite/:inviteCode" element={<InviteLandingPage />} />
      
      {/* Cash Game Invite - Join via SMS link */}
      <Route path="/join-game/:inviteCode" element={
        <JoinGamePageWrapper />
      } />
      
      {/* Tournament Onboarding - First-time cash game setup */}
      <Route path="/tournament-onboarding" element={
        <ProtectedRoute>
          <TournamentOnboarding onComplete={() => window.history.back()} />
        </ProtectedRoute>
      } />
      
      {/* Prize Wallet - View balance, withdraw funds */}
      <Route path="/wallet" element={
        <ProtectedRoute>
          <PrizeWalletPage />
        </ProtectedRoute>
      } />
      
      {/* Wallet Mockups - Design preview */}
      <Route path="/wallet-mockups" element={<WalletMockups />} />
      
      {/* Golf Coins - Loyalty Rewards Program */}
      <Route path="/golf-coins" element={
        <ProtectedRoute>
          <GolfCoinsPage user={user} />
        </ProtectedRoute>
      } />
      
      {/* Achievements - Badges & Milestones */}
      <Route path="/achievements" element={
        <ProtectedRoute>
          <AchievementsPage user={user} />
        </ProtectedRoute>
      } />
      
      {/* Leaderboard - Weekly Coin Rankings */}
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <LeaderboardPage user={user} />
        </ProtectedRoute>
      } />
      
      {/* Golfer Discovery - Dating app style golfer browsing */}
      <Route path="/discover" element={
        <ProtectedRoute>
          <GolferDiscovery user={user} />
        </ProtectedRoute>
      } />
      
      {/* PWA Share Target - Receives shares from other apps */}
      <Route path="/share" element={<ShareTargetPage user={user} />} />
      
      {/* Hook Feed - Par Seeker Reels (preserved for later development) */}
      <Route path="/hook" element={<HookFeed onBack={() => window.history.back()} />} />
      {/* Book Tee Times - Tee Sheet Management */}
      <Route path="/proshop/register" element={<ProtectedRoute><DarkDashboardLayout><CourseRegistrationPage /></DarkDashboardLayout></ProtectedRoute>} />
      <Route path="/proshop/payouts" element={<ProtectedRoute><DarkDashboardLayout><CoursePayoutsPage /></DarkDashboardLayout></ProtectedRoute>} />
      <Route path="/proshop/admin" element={<ProShopAdminPage />} />
      <Route path="/book-tee-time" element={<Navigate to="/book" replace />} />
      <Route path="/marketplace/connect/complete" element={<Navigate to="/proshop/payouts?stripe=return" replace />} />
      <Route path="/marketplace/connect/refresh" element={<Navigate to="/proshop/payouts?stripe=refresh" replace />} />
      <Route path="/book" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <BookTeeTimePageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* TEST: Golf GIF Generator - Standalone Test Page */}
      <Route path="/test/gif-generator" element={<GolfGifGenerator />} />
      {/* Golf Shots - Simple video to GIF */}
      <Route path="/test/golf-shots" element={<GolfShots />} />
      {/* NEW: Dark Theme Home Route - Dashboard with Action Buttons */}
      <Route path="/home" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <DashboardPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* Existing posts page - accessed through the 19th feature card */}
      <Route path="/feed" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <SocialFeedPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* PILOT PREVIEW: New Home Layout */}
      <Route path="/home-preview" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <HomePreviewWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <SubscriptionPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <SubscriptionSuccessPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <DashboardPageWrapper />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/add-score" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <AddScorePageWrapper />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/tournaments" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <TournamentsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/games" element={<LegacyGamesRedirect />} />
      {/* Social Events Routes */}
      <Route path="/events" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <EventsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <EventDetailPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* Groups Routes */}
      <Route path="/groups" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <GroupsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <GroupDetailPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* Friends Route */}
      <Route path="/friends" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <FriendsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* Find Golfers Hub - New utility-focused golfer search */}
      <Route path="/find-golfers" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <FindGolfersHubWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* Legacy /buddies route - redirect to /friends with requests tab */}
      <Route path="/buddies" element={
        <Navigate to="/friends?tab=requests" replace />
      } />
      {/* Chat/Messenger Route */}
      <Route path="/chat" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <ChatPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* Buy/Sell Marketplace Route */}
      <Route path="/buysell" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <BuySellPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* AI Tools Route */}
      <Route path="/ai-tools" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <AIToolsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/settlements" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <MySettlementsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/tournaments/match-play" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <MatchPlayPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/tournaments/skins" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <SkinsPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/tournaments/stroke-play" element={
        <ProtectedRoute>
          <DarkDashboardLayout>
            <StrokePlayPageWrapper />
          </DarkDashboardLayout>
        </ProtectedRoute>
      } />
      {/* /golf-connect route removed - replaced with notifications dropdown */}
      <Route path="/stats" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <StatsPage />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/history" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <HistoryPageWrapper />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/settings" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <SettingsPageWrapper />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      {/* User Profile Page - View any user's full profile */}
      <Route path="/profile/:userId" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <ProfilePageWrapper />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      {/* Hidden test route for QuickCard prototype */}
      <Route path="/test-quickcard" element={
        <SubscribedRoute>
          <TestQuickCard />
        </SubscribedRoute>
      } />
      {/* Group Round Live - with QuickCard */}
      <Route path="/group-round/:roundId" element={
        <SubscribedRoute>
          <GroupRoundLive user={user} />
        </SubscribedRoute>
      } />
      {/* Scorecard Navigator - Dev Tool */}
      <Route path="/dev/scorecards" element={
        <SubscribedRoute>
          <ScorecardNavigator />
        </SubscribedRoute>
      } />
      {/* Admin Dashboard - Not behind SubscribedRoute, has its own auth */}
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/support" element={<ProtectedRoute><DarkDashboardLayout><SupportPage /></DarkDashboardLayout></ProtectedRoute>} />
      <Route path="/support/:threadId" element={<ProtectedRoute><DarkDashboardLayout><SupportPage /></DarkDashboardLayout></ProtectedRoute>} />
      <Route path="/announcements/:notificationId" element={<ProtectedRoute><DarkDashboardLayout><AnnouncementPage /></DarkDashboardLayout></ProtectedRoute>} />
      {/* Email Templates - Admin only */}
      <Route path="/admin/email-templates" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <EmailTemplatesPageWrapper />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      
      {/* ==================== GOLF OUTING MANAGEMENT MODULE ==================== */}
      {/* Modular routes - can be extracted for standalone outing management app */}
      <Route path="/outings" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <OutingsPage user={user} />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/outings/create" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <OutingSetupWizard user={user} />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/outings/:outingId" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <OutingDetailPage user={user} />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/outings/:outingId/leaderboard" element={
        <SubscribedRoute>
          <DarkDashboardLayout>
            <OutingLeaderboardPage />
          </DarkDashboardLayout>
        </SubscribedRoute>
      } />
      <Route path="/outings/:outingId/scorecard/:teamId" element={
        <SubscribedRoute>
          <CaptainScorecardPage user={user} />
        </SubscribedRoute>
      } />
      {/* Public join page - no auth required */}
      <Route path="/outings/join/:outingId" element={
        <JoinOutingPage user={user} />
      } />
      
      {/* Tee Time Marketplace */}
      <Route path="/marketplace" element={
        <TeeTimeMarketplace user={user} />
      } />
      <Route path="/marketplace/course" element={
        <TeeTimeMarketplace user={user} isCourseView={true} />
      } />
    </Routes>
  );
}

function App() {
  // Initialize theme and font size from localStorage on app load
  useEffect(() => {
    const root = document.documentElement;
    
    // Preserve the saved preference; Light is the default for new browsers.
    const savedTheme = getThemePreference();
    root.classList.remove('theme-dark', 'theme-light');
    root.classList.add(`theme-${savedTheme}`);
    
    // Apply saved font size (default to small)
    const savedFontSize = localStorage.getItem('ps-font-size') || 'small';
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${savedFontSize}`);
  }, []);

  return (
    <BrowserRouter>
      <AdminAccessProvider>
      <AuthProvider>
        <LocationProvider>
          <AppRouter />
          <GifProgressIndicator 
            onGifReady={(gif) => {
              // Navigate to view the GIF or open a modal
              window.open(gif.gif_url, '_blank');
            }}
          />
          <Toaster position="top-center" richColors />
        </LocationProvider>
      </AuthProvider>
      </AdminAccessProvider>
    </BrowserRouter>
  );
}

export default App;

// Export shared components for use in extracted page components
export { AddPlayerModal, AddPlayersModal, CreateTeamModal };
